import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, FileText, ListOrdered } from 'lucide-react';
import { Heading } from '../layout';
import { TabNavigation } from '../reusable';
import QuotationsTable3 from './QuotationsTable3';
import QuotationItemsTable3 from './QuotationItemsTable3';
import PeriodSummaryTable from './PeriodSummaryTable';
import RegionSegmentStackedChart, {
  PRODUCT_LINE_DIMENSION,
  REGION_FILTER_DIMENSION,
  SEGMENT_STACK_DIMENSION,
  normalizeCommercialSegment,
} from './RegionSegmentStackedChart';
import MonthlyDistributionChart from './MonthlyDistributionChart';
import {
  CHART_METRIC_AMOUNT,
  CHART_METRIC_ADJUSTED_SALE_RATE,
  CHART_METRIC_COUNT,
  CHART_METRIC_GALLONS,
  CHART_METRIC_ITEMS,
  CHART_METRIC_SALE_RATE,
  resolvePricePerGallon,
  resolveTcoatLineGallons,
} from './chartFilterUtils';
import { QuotationsForm } from '../forms/QuotationsForm';
import { QuotationSummaryForm } from '../forms/QuotationSummaryForm';
import { QuotationItemsForm } from '../forms/QuotationItemsForm';
import { OrganizationsForm, serializeOrganizationChannels } from '../forms/OrganizationsForm';
import { ContactsForm } from '../forms/ContactsForm';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useUIColors } from '../../hooks/useTheme';
import { normalizeProperCaseText } from '../../utils/textUtils';
import { buildQuotationPdfFilename, drawQuotationContactHeader } from '../../utils/quotationPdf';
import { getActiveChannelOptions } from './commercialCatalogDefaults';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  collectDates,
  getAvailableYearsFromDates,
  getMonthsInRange,
  resolvePeriodSelection,
  isDateInRange,
  isDateInSelectedPeriod,
  parseDateSafe,
  periodSelectStyle,
  periodLabelStyle,
} from './periodSummaryUtils';

const PDF_PRODUCT_IMAGE_SIZE_OPTIONS = [
  { value: '24', label: 'Pequeño (24 mm)' },
  { value: '36', label: 'Mediano (36 mm)' },
  { value: '48', label: 'Grande (48 mm)' },
  { value: '60', label: 'Extra grande (60 mm)' },
];

const DEFAULT_VALIDITY_DAYS = 30;

const DEFAULT_PAYMENT_TERMS = 'Credito 30 Dias';

const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Seleccionar condición de pago' },
  { value: 'Contado', label: 'Contado' },
  { value: 'Credito 30 Dias', label: 'Crédito 30 días' },
  { value: 'Credito 45 Dias', label: 'Crédito 45 días' },
  { value: 'Credito 60 Dias', label: 'Crédito 60 días' },
  { value: 'Credito 90 Dias', label: 'Crédito 90 días' },
  { value: 'Anticipo 50% / Saldo contra entrega', label: 'Anticipo 50% / Saldo contra entrega' },
  { value: 'Anticipo 100%', label: 'Anticipo 100%' },
  { value: 'Contra entrega', label: 'Contra entrega' },
];

const STATUS_NORMALIZATION_MAP = {
  Enviada: 'Emitida',
  Aprobada: 'Adjudicada',
};

const getQuotationAnchorDate = (quotation) =>
  quotation?.quotation_date || quotation?.created_at || '';

const formatSummaryCount = (value) => Number(value || 0).toLocaleString('es-CL');

const formatSummaryMoney = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '$0';
  return `$${Math.round(n).toLocaleString('es-CL')}`;
};

/** Total neto → total con IVA (19%). */
const grossAmountConIva = (netTotal) => {
  const t = Number(netTotal || 0);
  if (!Number.isFinite(t) || t === 0) return 0;
  return Math.round(t * 1.19 * 100) / 100;
};

const formatSummaryPercent = (value) => `${Number(value || 0).toFixed(0)}%`;

const isClpLikeCurrency = (currency) => {
  const c = String(currency || 'CLP').trim().toUpperCase();
  return !c || c === 'CLP' || c === 'PESOS';
};

const isTcoatQuotationItem = (item = {}, paintSkuSet = null, upsellingSkuSet = null) => {
  const sku = String(item.product_sku || '').trim();
  const skuKey = sku.toUpperCase();
  const name = String(item.item_name || item.product_name || '');
  if (skuKey && upsellingSkuSet && upsellingSkuSet.has(skuKey)) return false;
  if (skuKey && paintSkuSet && paintSkuSet.has(skuKey)) return true;
  if (/t[\s-]?coat/i.test(name)) return true;
  // Sin catálogos: no asumir que cualquier SKU es T-COAT.
  if (paintSkuSet || upsellingSkuSet) return false;
  return Boolean(skuKey);
};

const resolveQuotationLineNet = (item = {}) => {
  const lineTotal = Number(item.line_total);
  if (Number.isFinite(lineTotal)) return lineTotal;
  const qty = Number(item.quantity || 0);
  const unitPrice = Number(item.unit_price || 0);
  const discount = Math.max(Math.min(Number(item.discount_percent || 0), 100), 0);
  if (!Number.isFinite(qty) || !Number.isFinite(unitPrice)) return 0;
  return qty * unitPrice * (1 - discount / 100);
};

const QUOTATION_CHART_VALUE_METRICS = [
  CHART_METRIC_COUNT,
  CHART_METRIC_ITEMS,
  CHART_METRIC_AMOUNT,
  CHART_METRIC_GALLONS,
  CHART_METRIC_SALE_RATE,
  CHART_METRIC_ADJUSTED_SALE_RATE,
];

const QUOTATION_CHART_PRODUCT_FILTERS = [PRODUCT_LINE_DIMENSION];

const SUMMARY_METRIC_OPTIONS = [
  { value: 'count', label: 'Cantidad de cotizaciones' },
  { value: 'amount', label: 'Montos cotizados' },
  { value: 'gallons', label: 'Galones T-COAT' },
];

const SUMMARY_SEGMENT_ORDER = ['B2G', 'B2B', 'B2C', 'B2B2C', 'Sin Asignar'];

const normalizeLookupKey = (value = '') => String(value || '').trim().toLowerCase();

const resolveValidityDays = (value, fallback = DEFAULT_VALIDITY_DAYS) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const resolveQuotationCodeForPayload = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed || trimmed === 'Se asigna automáticamente') return '';
  return trimmed.replace(/\s+/g, '').toUpperCase();
};

const pickDefaultFormatUnit = (formats = []) => {
  const list = (Array.isArray(formats) ? formats : []).filter((row) =>
    String(row.display_name || '').trim()
  );
  const preferred = list.find(
    (row) => normalizeLookupKey(row.display_name) === normalizeLookupKey('Tineta 16 L')
  );
  if (preferred) return String(preferred.display_name).trim();
  return list[0] ? String(list[0].display_name).trim() : '';
};

const resolveFormatDisplayName = (formats = [], preferredName = '') => {
  const key = normalizeLookupKey(preferredName);
  if (!key) return pickDefaultFormatUnit(formats);
  const match = (Array.isArray(formats) ? formats : []).find(
    (row) => normalizeLookupKey(row.display_name) === key
  );
  return match ? String(match.display_name).trim() : String(preferredName).trim();
};

const newQuotationLineRowKey = () =>
  `ql-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const buildEmptyQuotationLineItem = (defaultUnit = '') => ({
  row_key: newQuotationLineRowKey(),
  product_sku: '',
  item_name: '',
  color: '',
  color_code: '',
  quantity: '',
  unit: defaultUnit,
  unit_price: '',
  discount_percent: '0',
  status: 'Activo',
});

const resolveProductImageUrl = (imagePath) => {
  const raw = String(imagePath || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const relative = raw.replace(/^\/+/, '').replace(/^uploads\//, '');
  return `${API_BASE_URL}/uploads/${relative}`;
};

const loadImageDataUrlForPdf = async (url) => {
  if (!url) return null;
  try {
    const response = await authFetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = image.width || 1;
          canvas.height = image.height || 1;
          const context = canvas.getContext('2d');
          if (!context) {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
            return;
          }
          context.drawImage(image, 0, 0);
          URL.revokeObjectURL(objectUrl);
          resolve({
            dataUrl: canvas.toDataURL('image/jpeg', 0.9),
            width: image.width,
            height: image.height,
          });
        } catch (error) {
          URL.revokeObjectURL(objectUrl);
          resolve(null);
        }
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      image.src = objectUrl;
    });
  } catch (error) {
    return null;
  }
};

const collectQuotedPaintProductImages = (items, paintProducts) => {
  const imagePathBySku = new Map();
  const imagePathByName = new Map();
  (paintProducts || []).forEach((product) => {
    const sku = String(product.sku || '').trim().toUpperCase();
    const path = String(product.image_path || '').trim();
    if (sku && path && !imagePathBySku.has(sku)) {
      imagePathBySku.set(sku, path);
    }
    const key = normalizeLookupKey(product.product_name);
    if (key && path && !imagePathByName.has(key)) {
      imagePathByName.set(key, path);
    }
  });

  const entries = [];
  const seen = new Set();
  (items || []).forEach((item) => {
    const sku = String(item.product_sku || '').trim().toUpperCase();
    const nameKey = normalizeLookupKey(item.item_name);
    const imagePath = (sku && imagePathBySku.get(sku)) || (nameKey ? imagePathByName.get(nameKey) : '');
    const seenKey = sku || nameKey;
    if (!imagePath || !seenKey || seen.has(seenKey)) return;
    seen.add(seenKey);
    entries.push({ url: resolveProductImageUrl(imagePath) });
  });
  return entries;
};

const ensurePdfVerticalSpace = (doc, y, neededMm, marginBottom = 16, marginTop = 16) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + neededMm <= pageHeight - marginBottom) return y;
  doc.addPage();
  return marginTop;
};

const drawQuotedProductImagesOnPdf = async (
  doc,
  items,
  paintProducts,
  startY,
  pageWidth,
  maxHeightMm = 48
) => {
  const imageEntries = collectQuotedPaintProductImages(items, paintProducts);
  if (!imageEntries.length) return startY;

  const loadedImages = [];
  for (const entry of imageEntries) {
    const imageData = await loadImageDataUrlForPdf(entry.url);
    if (imageData?.dataUrl) loadedImages.push(imageData);
  }
  if (!loadedImages.length) return startY;

  const marginX = 14;
  const availableWidth = pageWidth - marginX * 2;
  const gap = 3;
  const maxHeight = Math.max(12, Number(maxHeightMm) || 48);
  // Mantener proporción: varias filas en vez de comprimir todas en una sola.
  const preferredSlotWidth = Math.max(28, Math.min(maxHeight * 1.25, 52));
  const perRow = Math.max(1, Math.floor((availableWidth + gap) / (preferredSlotWidth + gap)));
  const slotWidth = Math.min(
    preferredSlotWidth,
    (availableWidth - gap * Math.max(perRow - 1, 0)) / perRow
  );

  let x = marginX;
  let y = ensurePdfVerticalSpace(doc, startY, maxHeight + 4);
  let col = 0;
  let rowBottom = y;

  loadedImages.forEach((imageData, index) => {
    if (col >= perRow) {
      col = 0;
      x = marginX;
      y = ensurePdfVerticalSpace(doc, rowBottom + 3, maxHeight + 4);
      rowBottom = y;
    }

    let drawW = slotWidth;
    let drawH = maxHeight;
    if (imageData.width && imageData.height) {
      const ratio = imageData.width / imageData.height;
      drawH = maxHeight;
      drawW = drawH * ratio;
      if (drawW > slotWidth) {
        drawW = slotWidth;
        drawH = drawW / ratio;
      }
    }
    const drawY = y + (maxHeight - drawH) / 2;
    doc.addImage(imageData.dataUrl, 'JPEG', x + (slotWidth - drawW) / 2, drawY, drawW, drawH);
    x += slotWidth + gap;
    rowBottom = Math.max(rowBottom, drawY + drawH);
    col += 1;

    if (index === loadedImages.length - 1) {
      y = rowBottom;
    }
  });

  return rowBottom + 4;
};

const catalogEntryForSku = (sku, paintRows, upsellingRows) => {
  const key = String(sku || '').trim();
  if (!key) return null;
  const upper = key.toUpperCase();
  const paint = (paintRows || []).find((row) => String(row.sku || '').trim().toUpperCase() === upper);
  if (paint) {
    return {
      sku: String(paint.sku || '').trim(),
      name: String(paint.product_name || '').trim(),
      kind: 'paint',
      unit_price: '',
    };
  }
  const upselling = (upsellingRows || []).find((row) => {
    const code = String(row.upselling_code || '').trim();
    return code === key || code.toUpperCase() === upper;
  });
  if (upselling) {
    return {
      sku: String(upselling.upselling_code || '').trim(),
      name: String(upselling.product_name || '').trim(),
      kind: 'upselling',
      unit_price: Number(upselling.base_price || 0) || '',
    };
  }
  return null;
};

const normalizeColorDisplayName = (rawName = '') => {
  const input = String(rawName || '').trim();
  if (!input) return '';
  if (/\(s[íi]mil a\s+(ral|sw)\s*\d{3,4}\)/i.test(input)) {
    return input;
  }

  const ralMatch = input.match(/^RAL\s*(\d{4})\s*[-:]\s*(.+)$/i);
  if (ralMatch) {
    const ralCode = ralMatch[1];
    const baseName = String(ralMatch[2] || '').trim();
    return baseName ? `${baseName} (Símil a RAL ${ralCode})` : input;
  }

  const swMatch = input.match(/^(.+?)\s*[-(]?\s*SW\s*(\d{3,4})\)?$/i);
  if (swMatch) {
    const baseName = String(swMatch[1] || '').trim().replace(/[-(]\s*$/, '');
    const swCode = swMatch[2];
    return baseName ? `${baseName} (Símil a SW ${swCode})` : input;
  }

  return input;
};

const normalizeQuotationStatus = (status = '') =>
  STATUS_NORMALIZATION_MAP[String(status || '').trim()] || String(status || '').trim() || 'Borrador';

const QUOTATION_DRAFT_AUTOSAVE_KEY = 'quotation_draft_autosave_enabled';
const QUOTATION_DRAFT_AUTOSAVE_MS = 2500;

const readDraftAutosaveEnabled = () => {
  try {
    const raw = window.localStorage.getItem(QUOTATION_DRAFT_AUTOSAVE_KEY);
    if (raw === null) return true;
    return raw !== '0' && raw !== 'false';
  } catch {
    return true;
  }
};

const getUserInitials = (fullName = '') => {
  const words = String(fullName)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '-';
  if (words.length === 1) return words[0];
  const firstName = words[0];
  const lastName = words[words.length - 1];
  const initial = (firstName[0] || '').toUpperCase();
  return `${initial}. ${lastName}`;
};

const PaintQuotationsWorkspace = ({ isDarkMode = false }) => {
  const { user: sessionUser } = useAuth();
  const [quotationsData, setQuotationsData] = useState([]);
  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState('all');
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [chartsCollapsed, setChartsCollapsed] = useState(false);
  const [summaryMetric, setSummaryMetric] = useState('count');
  const [summarySegmentVisibility, setSummarySegmentVisibility] = useState({});
  const [quotationsLoading, setQuotationsLoading] = useState(true);
  const [quotationsError, setQuotationsError] = useState('');
  const [showQuotationFormModal, setShowQuotationFormModal] = useState(false);
  const [showQuotationSummaryModal, setShowQuotationSummaryModal] = useState(false);
  const [summaryQuotation, setSummaryQuotation] = useState(null);
  const [summaryOrganizationUnits, setSummaryOrganizationUnits] = useState([]);
  const [isClosingQuotation, setIsClosingQuotation] = useState(false);
  const [editingQuotationId, setEditingQuotationId] = useState(null);
  const [isSavingQuotation, setIsSavingQuotation] = useState(false);
  const [quotationFormError, setQuotationFormError] = useState('');
  /** Errores de validación al guardar: organización, usuario, ítems por índice, mensaje general de ítems. */
  const [quotationFieldErrors, setQuotationFieldErrors] = useState({});
  const [draftAutosaveEnabled, setDraftAutosaveEnabled] = useState(readDraftAutosaveEnabled);
  const [draftAutosaveStatus, setDraftAutosaveStatus] = useState('idle');
  const [draftAutosaveMessage, setDraftAutosaveMessage] = useState('');
  const [quotationFormDirty, setQuotationFormDirty] = useState(false);
  const quotationSaveLockRef = useRef(false);
  const editingQuotationIdRef = useRef(null);
  const quotationFormDataRef = useRef(null);
  const draftAutosaveEnabledRef = useRef(draftAutosaveEnabled);
  const quotationFormDirtyRevRef = useRef(0);
  const [organizationsData, setOrganizationsData] = useState([]);
  const [contactsData, setContactsData] = useState([]);
  const [competitorsData, setCompetitorsData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [paintProductsData, setPaintProductsData] = useState([]);
  const [paintColorsData, setPaintColorsData] = useState([]);
  const [paintFormatsData, setPaintFormatsData] = useState([]);
  const [upsellingProductsData, setUpsellingProductsData] = useState([]);
  const [upsellingCategoryOptions, setUpsellingCategoryOptions] = useState([]);

  const [itemsData, setItemsData] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState('');
  const [showItemFormModal, setShowItemFormModal] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [isSavingItem, setIsSavingItem] = useState(false);
  const [itemFormError, setItemFormError] = useState('');

  const getTodayISO = () => new Date().toISOString().slice(0, 10);

  /** Filas con producto son válidas con cantidad/precio >= 0. Cero dispara warning (no bloquea). */
  const validateQuotationLineItems = (items) => {
    const list = Array.isArray(items) ? items : [];
    const complete = [];
    const itemErrors = {};
    const itemWarnings = {};
    let hasZeroValues = false;
    for (let i = 0; i < list.length; i += 1) {
      const item = list[i];
      const sku = String(item.product_sku || '').trim();
      const name = String(item.item_name || '').trim();
      const qtyBlank = String(item.quantity ?? '').trim() === '';
      const priceBlank = String(item.unit_price ?? '').trim() === '';
      const qty = qtyBlank ? 0 : Number(item.quantity);
      const price = priceBlank ? 0 : Number(item.unit_price);
      const rowEmpty = !sku && !name && qtyBlank && priceBlank;
      if (rowEmpty) continue;

      const errs = {};
      const warns = {};
      if (!sku) errs.item_name = 'Selecciona un producto del catálogo (código T-COAT o upselling).';
      if (!Number.isFinite(qty) || qty < 0) {
        errs.quantity = 'La cantidad no puede ser negativa.';
      } else if (qty === 0) {
        warns.quantity = 'Cantidad en 0.';
        hasZeroValues = true;
      }
      if (!Number.isFinite(price) || price < 0) {
        errs.unit_price = 'El precio unitario no puede ser negativo.';
      } else if (price === 0) {
        warns.unit_price = 'Precio unitario en 0.';
        hasZeroValues = true;
      }
      if (Object.keys(errs).length > 0) {
        itemErrors[i] = errs;
      } else {
        complete.push(item);
        if (Object.keys(warns).length > 0) itemWarnings[i] = warns;
      }
    }
    let itemsGeneral = '';
    if (complete.length === 0 && Object.keys(itemErrors).length === 0) {
      itemsGeneral = 'Incluye al menos un ítem con producto.';
    }
    const ok = complete.length > 0 && Object.keys(itemErrors).length === 0;
    return { ok, items: complete, itemErrors, itemWarnings, itemsGeneral, hasZeroValues };
  };

  const confirmZeroValueItems = (itemsCheck) => {
    if (!itemsCheck?.hasZeroValues) return true;
    return window.confirm(
      'Hay ítems con cantidad o precio unitario en 0.\n\n¿Deseas continuar de todas formas?'
    );
  };

  const EMPTY_QUOTATION_FORM = {
    quotation_code: 'Se asigna automáticamente',
    organization_id: '',
    contact_id: '',
    user_id: '',
    equivalent_gallons: '0',
    quotation_date: getTodayISO(),
    validity_days: String(DEFAULT_VALIDITY_DAYS),
    tender: '',
    channel: '',
    delivery_days: '0',
    delivery_place: 'Instalaciones del Cliente',
    delivery_method: 'Despacho',
    payment_terms: DEFAULT_PAYMENT_TERMS,
    closing_date: '',
    currency: 'CLP',
    status: 'Borrador',
    comments_text: '',
    observations_text: '',
    pdf_include_product_images: true,
    pdf_product_image_size_mm: '48',
    quotation_items: [buildEmptyQuotationLineItem('')],
  };

  const EMPTY_ITEM_FORM = {
    item_code: 'Se asigna automáticamente',
    quotation_id: '',
    ...buildEmptyQuotationLineItem(''),
  };

  const [quotationFormData, setQuotationFormData] = useState(EMPTY_QUOTATION_FORM);
  const [itemFormData, setItemFormData] = useState(EMPTY_ITEM_FORM);

  useEffect(() => {
    quotationFormDataRef.current = quotationFormData;
  }, [quotationFormData]);

  useEffect(() => {
    editingQuotationIdRef.current = editingQuotationId;
  }, [editingQuotationId]);

  useEffect(() => {
    draftAutosaveEnabledRef.current = draftAutosaveEnabled;
  }, [draftAutosaveEnabled]);

  const markQuotationFormDirty = () => {
    quotationFormDirtyRevRef.current += 1;
    setQuotationFormDirty(true);
    setDraftAutosaveStatus((prev) => (prev === 'saving' ? prev : 'pending'));
  };

  const handleDraftAutosaveToggle = (enabled) => {
    setDraftAutosaveEnabled(enabled);
    try {
      window.localStorage.setItem(QUOTATION_DRAFT_AUTOSAVE_KEY, enabled ? '1' : '0');
    } catch {
      /* ignore */
    }
    if (!enabled) {
      setDraftAutosaveStatus('idle');
      setDraftAutosaveMessage('Autoguardado desactivado');
    } else {
      setDraftAutosaveMessage('');
      setDraftAutosaveStatus(quotationFormDirty ? 'pending' : 'idle');
    }
  };
  const [showQuickOrgFormModal, setShowQuickOrgFormModal] = useState(false);
  const [isSavingQuickOrg, setIsSavingQuickOrg] = useState(false);
  const [quickOrgFormError, setQuickOrgFormError] = useState('');
  const [showQuickContactFormModal, setShowQuickContactFormModal] = useState(false);
  const [isSavingQuickContact, setIsSavingQuickContact] = useState(false);
  const [quickContactFormError, setQuickContactFormError] = useState('');
  const [quickContactFormData, setQuickContactFormData] = useState({
    contact_code: 'Se asigna automáticamente',
    full_name: '',
    role: '',
    email: '',
    linkedin: '',
    phone: '',
    organization_id: '',
    status: 'Activo',
  });
  const [quickOrgFormData, setQuickOrgFormData] = useState({
    org_code: 'Se asigna automáticamente',
    org_name: '',
    rut: '',
    segment: 'B2B',
    channel: '',
    created_by: '',
    website: '',
    linkedin: '',
    industry: '',
    subindustry: '',
    email: '',
    phone: '',
    address: '',
    commune: '',
    region: '',
    country: '',
    contacts: [],
    units: [],
  });

  const quotationUserFilterOptions = useMemo(
    () =>
      usersData.map((user) => ({
        value: String(user.id),
        label: user.full_name || user.user_code || `USR ${user.id}`,
      })),
    [usersData]
  );

  const quotationOrganizationFilterOptions = useMemo(
    () =>
      organizationsData.map((org) => ({
        value: String(org.id),
        label: org.org_name || `ORG ${org.id}`,
      })),
    [organizationsData]
  );

  const paintSkuSet = useMemo(() => {
    const set = new Set();
    (paintProductsData || []).forEach((row) => {
      const key = String(row.sku || '').trim().toUpperCase();
      if (key) set.add(key);
    });
    return set;
  }, [paintProductsData]);

  const upsellingSkuSet = useMemo(() => {
    const set = new Set();
    (upsellingProductsData || []).forEach((row) => {
      [row.upselling_code, row.legacy_code, row.legacy_code2].forEach((code) => {
        const key = String(code || '').trim().toUpperCase();
        if (key) set.add(key);
      });
    });
    return set;
  }, [upsellingProductsData]);

  const quotationRegionChartItems = useMemo(() => {
    const quoteById = new Map(quotationsData.map((quotation) => [Number(quotation.id), quotation]));
    const orgById = new Map(
      organizationsData.map((organization) => [Number(organization.id), organization])
    );
    const userById = new Map(usersData.map((user) => [Number(user.id), user]));
    const quotedIds = new Set();

    const buildMeta = (quotation) => {
      const organization = orgById.get(Number(quotation.organization_id));
      const user = userById.get(Number(quotation.user_id));
      return {
        region: organization?.region || '',
        segment: organization?.segment || '',
        status: normalizeQuotationStatus(quotation.status),
        executive: user?.full_name || quotation.user_name || 'Sin ejecutivo',
        date: getQuotationAnchorDate(quotation),
        quotationId: Number(quotation.id),
        quotationAmount: isClpLikeCurrency(quotation.currency)
          ? grossAmountConIva(quotation.total_amount)
          : 0,
      };
    };

    const lineItems = (itemsData || []).flatMap((item) => {
      const quotation = quoteById.get(Number(item.quotation_id));
      if (!quotation) return [];
      quotedIds.add(Number(quotation.id));
      const isTcoat = isTcoatQuotationItem(item, paintSkuSet, upsellingSkuSet);
      const lineNet = resolveQuotationLineNet(item);
      const gallons = isTcoat ? resolveTcoatLineGallons(item) : 0;
      const discount = Math.max(Math.min(Number(item.discount_percent || 0), 100), 0);
      const netUnitPrice = Number(item.unit_price || 0) * (1 - discount / 100);
      return [
        {
          ...buildMeta(quotation),
          productLine: isTcoat ? 'T-COAT' : 'Upselling',
          productName:
            catalogEntryForSku(item.product_sku, paintProductsData, upsellingProductsData)?.name ||
            String(item.item_name || item.product_name || '').trim() ||
            'Sin producto',
          amount: isClpLikeCurrency(quotation.currency) ? grossAmountConIva(lineNet) : 0,
          gallons,
          pricePerGallon: gallons > 0 ? resolvePricePerGallon(netUnitPrice, item.unit) : 0,
        },
      ];
    });

    // Incluye cotizaciones sin ítems para que "Cantidad" cuadre con el total real.
    const emptyQuoteItems = quotationsData.flatMap((quotation) => {
      const id = Number(quotation.id);
      if (!id || quotedIds.has(id)) return [];
      return [
        {
          ...buildMeta(quotation),
          productLine: 'Sin ítems',
          productName: 'Sin producto',
          amount: 0,
          gallons: 0,
          pricePerGallon: 0,
          isEmptyQuote: true,
        },
      ];
    });

    return [...lineItems, ...emptyQuoteItems];
  }, [quotationsData, organizationsData, usersData, itemsData, paintSkuSet, upsellingSkuSet, paintProductsData, upsellingProductsData]);

  const quotationChartStackDimensions = useMemo(
    () => [
      SEGMENT_STACK_DIMENSION,
      REGION_FILTER_DIMENSION,
      {
        key: 'status',
        label: 'Estado',
        order: ['Borrador', 'Emitida', 'Adjudicada', 'Vendida', 'Perdida', 'Rechazada', 'Cancelada', 'Desierta'],
        colors: {
          Borrador: '#9ca3af',
          Emitida: '#3b82f6',
          Adjudicada: '#16a34a',
          Vendida: '#0d9488',
          Perdida: '#d97706',
          Rechazada: '#db2777',
          Cancelada: '#dc2626',
          Desierta: '#6b7280',
        },
        fallback: 'Sin estado',
      },
      {
        key: 'executive',
        label: 'Ejecutivo',
        fallback: 'Sin ejecutivo',
      },
    ],
    []
  );

  const ui = useUIColors();

  const quotationDateValues = useMemo(
    () => collectDates(quotationsData, getQuotationAnchorDate),
    [quotationsData]
  );

  const availableYears = useMemo(
    () => getAvailableYearsFromDates(quotationDateValues),
    [quotationDateValues]
  );

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues: quotationDateValues,
        secondaryYear,
        secondaryMonth,
        titles: {
          monthly: 'Resumen cotizaciones mensual',
          annual: 'Resumen cotizaciones anual',
        },
      }),
    [periodFilter, quotationDateValues, secondaryYear, secondaryMonth]
  );

  /** Datos de gráficos acotados al rango del período seleccionado. */
  const quotationChartItems = useMemo(() => {
    const range = selectedPeriod?.range;
    if (!range) return quotationRegionChartItems;
    return quotationRegionChartItems.filter((item) => isDateInRange(item.date, range));
  }, [quotationRegionChartItems, selectedPeriod]);

  const quotationChartPeriods = useMemo(() => {
    if (selectedPeriod?.mode === 'year') {
      return selectedPeriod.columns || [];
    }
    const range = selectedPeriod?.range;
    if (range?.start && range?.end) {
      return getMonthsInRange(range.start, range.end);
    }
    return selectedPeriod?.columns || [];
  }, [selectedPeriod]);

  useEffect(() => {
    if (!availableYears.length) return;
    if (periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral') {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly') {
      if (!availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear(String(availableYears[availableYears.length - 1]));
      }
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (nextPeriod === 'annual' || nextPeriod === 'annual_ytd' || nextPeriod === 'quarterly' || nextPeriod === 'semestral') {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear));
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const filteredQuotationsData = useMemo(
    () =>
      quotationsData.filter((quotation) =>
        isDateInSelectedPeriod(getQuotationAnchorDate(quotation), selectedPeriod)
      ),
    [quotationsData, selectedPeriod]
  );

  const quotationSegmentById = useMemo(() => {
    const orgById = new Map(
      organizationsData.map((organization) => [Number(organization.id), organization])
    );
    const map = new Map();
    quotationsData.forEach((quotation) => {
      const organization = orgById.get(Number(quotation.organization_id));
      map.set(Number(quotation.id), normalizeCommercialSegment(organization?.segment));
    });
    return map;
  }, [quotationsData, organizationsData]);

  const summarySegmentOptions = useMemo(() => {
    const discovered = new Set();
    quotationSegmentById.forEach((segment) => discovered.add(segment));
    return [
      ...SUMMARY_SEGMENT_ORDER.filter((key) => discovered.has(key)),
      ...[...discovered].filter((key) => !SUMMARY_SEGMENT_ORDER.includes(key)).sort((a, b) => a.localeCompare(b, 'es')),
    ];
  }, [quotationSegmentById]);

  useEffect(() => {
    if (!summarySegmentOptions.length) return;
    setSummarySegmentVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      summarySegmentOptions.forEach((segment) => {
        if (next[segment] === undefined) {
          next[segment] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [summarySegmentOptions]);

  const handleToggleSummarySegment = (segment) => {
    setSummarySegmentVisibility((prev) => ({
      ...prev,
      [segment]: prev[segment] === false,
    }));
  };

  const handleShowAllSummarySegments = () => {
    setSummarySegmentVisibility(
      summarySegmentOptions.reduce((acc, segment) => {
        acc[segment] = true;
        return acc;
      }, {})
    );
  };

  const handleHideAllSummarySegments = () => {
    setSummarySegmentVisibility(
      summarySegmentOptions.reduce((acc, segment) => {
        acc[segment] = false;
        return acc;
      }, {})
    );
  };

  const tcoatGallonsByQuotationId = useMemo(() => {
    const map = new Map();
    (itemsData || []).forEach((item) => {
      if (!isTcoatQuotationItem(item, paintSkuSet, upsellingSkuSet)) return;
      const quotationId = Number(item.quotation_id);
      if (!quotationId) return;
      const gallons = resolveTcoatLineGallons(item);
      if (gallons <= 0) return;
      map.set(quotationId, (map.get(quotationId) || 0) + gallons);
    });
    return map;
  }, [itemsData, paintSkuSet, upsellingSkuSet]);

  const itemsTableRows = useMemo(() => {
    const orgByQuotationId = new Map(
      (quotationsData || []).map((q) => [Number(q.id), String(q.organization_name || '').trim()])
    );
    return (itemsData || []).map((item) => ({
      ...item,
      organization_name:
        String(item.organization_name || '').trim() ||
        orgByQuotationId.get(Number(item.quotation_id)) ||
        '',
    }));
  }, [itemsData, quotationsData]);

  const quotationSummaryRows = useMemo(() => {
    const periods = selectedPeriod.columns || [];
    const periodStart = periods[0]?.start || selectedPeriod.range?.start || null;
    const isSegmentVisible = (quotation) => {
      const segment = quotationSegmentById.get(Number(quotation.id)) || 'Sin Asignar';
      return summarySegmentVisibility[segment] !== false;
    };
    const scopedQuotations = quotationsData.filter(isSegmentVisible);

    const inBucket = (quotation, bucket) => {
      const date = parseDateSafe(getQuotationAnchorDate(quotation));
      return Boolean(date && date >= bucket.start && date <= bucket.end);
    };
    const upToBucket = (quotation, bucket) => {
      const date = parseDateSafe(getQuotationAnchorDate(quotation));
      if (!date || date > bucket.end) return false;
      if (periodStart && date < periodStart) return false;
      return true;
    };
    const statusOf = (quotation) => normalizeQuotationStatus(quotation.status);

    if (summaryMetric === 'amount') {
      const sumClpBy = (predicate) =>
        periods.map((bucket) =>
          scopedQuotations
            .filter((q) => inBucket(q, bucket) && isClpLikeCurrency(q.currency) && predicate(q))
            .reduce((acc, q) => acc + grossAmountConIva(q.total_amount), 0)
        );
      const cumSumClpBy = (predicate) =>
        periods.map((bucket) =>
          scopedQuotations
            .filter((q) => upToBucket(q, bucket) && isClpLikeCurrency(q.currency) && predicate(q))
            .reduce((acc, q) => acc + grossAmountConIva(q.total_amount), 0)
        );
      const montoCotizado = sumClpBy(() => true);
      const montoVendido = sumClpBy((q) => statusOf(q) === 'Vendida');
      const montoPerdido = sumClpBy((q) => statusOf(q) === 'Perdida');
      const tasa = periods.map((_, i) =>
        montoCotizado[i] ? (montoVendido[i] / montoCotizado[i]) * 100 : 0
      );
      const tasaAjustada = periods.map((_, i) => {
        const cerradasValidas = montoVendido[i] + montoPerdido[i];
        return cerradasValidas ? (montoVendido[i] / cerradasValidas) * 100 : 0;
      });
      const montoCotizadoAcum = cumSumClpBy(() => true);
      const montoVendidoAcum = cumSumClpBy((q) => statusOf(q) === 'Vendida');
      const montoPerdidoAcum = cumSumClpBy((q) => statusOf(q) === 'Perdida');
      const tasaAcum = periods.map((_, i) =>
        montoCotizadoAcum[i] ? (montoVendidoAcum[i] / montoCotizadoAcum[i]) * 100 : 0
      );
      const tasaAjustadaAcum = periods.map((_, i) => {
        const cerradasValidas = montoVendidoAcum[i] + montoPerdidoAcum[i];
        return cerradasValidas ? (montoVendidoAcum[i] / cerradasValidas) * 100 : 0;
      });
      return [
        { label: 'Monto cotizado c/IVA (CLP)', tone: 'neutral', values: montoCotizado.map(formatSummaryMoney) },
        { label: 'Monto emitido c/IVA (CLP)', tone: 'neutral', values: sumClpBy((q) => statusOf(q) === 'Emitida').map(formatSummaryMoney) },
        { label: 'Monto vendido c/IVA (CLP)', tone: 'client', values: montoVendido.map(formatSummaryMoney) },
        { label: 'Monto perdido c/IVA (CLP)', tone: 'prospect', values: montoPerdido.map(formatSummaryMoney) },
        {
          label: 'Monto no válido c/IVA (CLP)',
          tone: 'prospect',
          values: sumClpBy((q) => statusOf(q) === 'Cancelada' || statusOf(q) === 'Desierta').map(formatSummaryMoney),
        },
        { label: 'Tasa de venta', tone: 'total', values: tasa.map(formatSummaryPercent) },
        { label: 'Tasa de venta ajustada', tone: 'total', values: tasaAjustada.map(formatSummaryPercent) },
        { label: 'Monto cotizado acum. c/IVA (CLP)', tone: 'total', values: montoCotizadoAcum.map(formatSummaryMoney) },
        { label: 'Monto vendido acum. c/IVA (CLP)', tone: 'total', values: montoVendidoAcum.map(formatSummaryMoney) },
        { label: 'Tasa de venta (acum. período)', tone: 'total', values: tasaAcum.map(formatSummaryPercent) },
        { label: 'Tasa de venta ajustada (acum. período)', tone: 'total', values: tasaAjustadaAcum.map(formatSummaryPercent) },
      ];
    }

    if (summaryMetric === 'gallons') {
      const gallonsOf = (quotation) => tcoatGallonsByQuotationId.get(Number(quotation.id)) || 0;
      const sumGalBy = (predicate) =>
        periods.map((bucket) =>
          scopedQuotations
            .filter((q) => inBucket(q, bucket) && predicate(q))
            .reduce((acc, q) => acc + gallonsOf(q), 0)
        );
      const cumGalBy = (predicate) =>
        periods.map((bucket) =>
          scopedQuotations
            .filter((q) => upToBucket(q, bucket) && predicate(q))
            .reduce((acc, q) => acc + gallonsOf(q), 0)
        );
      const formatGal = (value) =>
        (Math.round(Number(value || 0) * 10) / 10).toLocaleString('es-CL', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        });
      const galCotizados = sumGalBy(() => true);
      const galVendidos = sumGalBy((q) => statusOf(q) === 'Vendida');
      const galPerdidos = sumGalBy((q) => statusOf(q) === 'Perdida');
      const tasa = periods.map((_, i) =>
        galCotizados[i] ? (galVendidos[i] / galCotizados[i]) * 100 : 0
      );
      const tasaAjustada = periods.map((_, i) => {
        const cerradasValidas = galVendidos[i] + galPerdidos[i];
        return cerradasValidas ? (galVendidos[i] / cerradasValidas) * 100 : 0;
      });
      const galCotizadosAcum = cumGalBy(() => true);
      const galVendidosAcum = cumGalBy((q) => statusOf(q) === 'Vendida');
      const galPerdidosAcum = cumGalBy((q) => statusOf(q) === 'Perdida');
      const tasaAcum = periods.map((_, i) =>
        galCotizadosAcum[i] ? (galVendidosAcum[i] / galCotizadosAcum[i]) * 100 : 0
      );
      const tasaAjustadaAcum = periods.map((_, i) => {
        const cerradasValidas = galVendidosAcum[i] + galPerdidosAcum[i];
        return cerradasValidas ? (galVendidosAcum[i] / cerradasValidas) * 100 : 0;
      });
      return [
        { label: 'Galones T-COAT cotizados', tone: 'neutral', values: galCotizados.map(formatGal) },
        { label: 'Galones emitidos', tone: 'neutral', values: sumGalBy((q) => statusOf(q) === 'Emitida').map(formatGal) },
        { label: 'Galones vendidos', tone: 'client', values: galVendidos.map(formatGal) },
        { label: 'Galones perdidos', tone: 'prospect', values: galPerdidos.map(formatGal) },
        {
          label: 'Galones no válidos',
          tone: 'prospect',
          values: sumGalBy((q) => statusOf(q) === 'Cancelada' || statusOf(q) === 'Desierta').map(formatGal),
        },
        { label: 'Tasa de venta', tone: 'total', values: tasa.map(formatSummaryPercent) },
        { label: 'Tasa de venta ajustada', tone: 'total', values: tasaAjustada.map(formatSummaryPercent) },
        { label: 'Galones acum. período', tone: 'total', values: galCotizadosAcum.map(formatGal) },
        { label: 'Galones vendidos acum. período', tone: 'total', values: galVendidosAcum.map(formatGal) },
        { label: 'Tasa de venta (acum. período)', tone: 'total', values: tasaAcum.map(formatSummaryPercent) },
        { label: 'Tasa de venta ajustada (acum. período)', tone: 'total', values: tasaAjustadaAcum.map(formatSummaryPercent) },
      ];
    }

    const countBy = (predicate) =>
      periods.map((bucket) => scopedQuotations.filter((q) => inBucket(q, bucket) && predicate(q)).length);
    const cumCountBy = (predicate) =>
      periods.map((bucket) => scopedQuotations.filter((q) => upToBucket(q, bucket) && predicate(q)).length);

    const nuevas = countBy(() => true);
    const vendidas = countBy((q) => statusOf(q) === 'Vendida');
    const perdidas = countBy((q) => statusOf(q) === 'Perdida');
    const tasa = periods.map((_, i) => (nuevas[i] ? (vendidas[i] / nuevas[i]) * 100 : 0));
    const tasaAjustada = periods.map((_, i) => {
      const cerradasValidas = vendidas[i] + perdidas[i];
      return cerradasValidas ? (vendidas[i] / cerradasValidas) * 100 : 0;
    });
    const nuevasAcum = cumCountBy(() => true);
    const vendidasAcum = cumCountBy((q) => statusOf(q) === 'Vendida');
    const perdidasAcum = cumCountBy((q) => statusOf(q) === 'Perdida');
    const tasaAcum = periods.map((_, i) =>
      nuevasAcum[i] ? (vendidasAcum[i] / nuevasAcum[i]) * 100 : 0
    );
    const tasaAjustadaAcum = periods.map((_, i) => {
      const cerradasValidas = vendidasAcum[i] + perdidasAcum[i];
      return cerradasValidas ? (vendidasAcum[i] / cerradasValidas) * 100 : 0;
    });

    return [
      { label: 'Nuevas cotizaciones', tone: 'neutral', values: nuevas.map(formatSummaryCount) },
      { label: 'Cotizaciones Emitidas', tone: 'neutral', values: countBy((q) => statusOf(q) === 'Emitida').map(formatSummaryCount) },
      { label: 'Cotizaciones Vendidas', tone: 'client', values: vendidas.map(formatSummaryCount) },
      { label: 'Cotizaciones Perdidas', tone: 'prospect', values: perdidas.map(formatSummaryCount) },
      {
        label: 'Cotizaciones no válidas',
        tone: 'prospect',
        values: countBy((q) => statusOf(q) === 'Cancelada' || statusOf(q) === 'Desierta').map(formatSummaryCount),
      },
      { label: 'Tasa de venta', tone: 'total', values: tasa.map(formatSummaryPercent) },
      { label: 'Tasa de venta ajustada', tone: 'total', values: tasaAjustada.map(formatSummaryPercent) },
      { label: 'Total cotizaciones (acum. período)', tone: 'total', values: nuevasAcum.map(formatSummaryCount) },
      { label: 'Total vendidas (acum. período)', tone: 'total', values: vendidasAcum.map(formatSummaryCount) },
      { label: 'Tasa de venta (acum. período)', tone: 'total', values: tasaAcum.map(formatSummaryPercent) },
      { label: 'Tasa de venta ajustada (acum. período)', tone: 'total', values: tasaAjustadaAcum.map(formatSummaryPercent) },
    ];
  }, [
    quotationsData,
    selectedPeriod,
    summaryMetric,
    summarySegmentVisibility,
    quotationSegmentById,
    tcoatGallonsByQuotationId,
  ]);

  const organizationOptions = [
    { value: '', label: 'Seleccionar organización', rut: '' },
    ...organizationsData.map((organization) => ({
      value: String(organization.id),
      label: organization.org_name || `ORG ${organization.id}`,
      rut: organization.rut || '',
    })),
  ];

  const channelOptions = getActiveChannelOptions();

  const selectedOrganizationId = Number(quotationFormData.organization_id || 0);
  const filteredContacts = selectedOrganizationId
    ? contactsData.filter((contact) => Number(contact.organization_id) === selectedOrganizationId)
    : [];
  const contactOptions = [
    {
      value: '',
      label: selectedOrganizationId
        ? 'Seleccionar contacto'
        : 'Selecciona una organización primero',
    },
    ...filteredContacts.map((contact) => ({
      value: String(contact.id),
      label: `${contact.contact_code} - ${contact.full_name}`,
    })),
  ];
  const usersForExecutive = useMemo(() => {
    if (!sessionUser?.id) return usersData;
    const sessionRow = {
      id: sessionUser.id,
      user_code: sessionUser.user_code,
      full_name: sessionUser.full_name,
      email: sessionUser.email,
      fono: sessionUser.fono || '',
      job_title: sessionUser.job_title,
      status: sessionUser.status || 'Activo',
    };
    const exists = usersData.some((u) => Number(u.id) === Number(sessionUser.id));
    return exists ? usersData : [sessionRow, ...usersData];
  }, [usersData, sessionUser]);

  const userOptions = [
    { value: '', label: 'Seleccionar ejecutivo' },
    ...usersForExecutive.map((user) => ({
      value: String(user.id),
      label: user.full_name || user.user_code || `USR ${user.id}`,
    })),
  ];
  const executiveOptionsForOrganizationsForm = [
    { value: '', label: 'Seleccionar ejecutivo' },
    ...usersForExecutive.map((user) => ({
      value: user.full_name || user.user_code || `USR ${user.id}`,
      label: user.full_name || user.user_code || `USR ${user.id}`,
    })),
  ];
  const itemDescriptionOptions = useMemo(() => {
    const paintOptions = (paintProductsData || [])
      .filter((product) => String(product.status || 'Activo').trim() !== 'Inactivo')
      .map((product) => {
        const sku = String(product.sku || '').trim();
        const name = String(product.product_name || '').trim();
        if (!sku || !name) return null;
        return {
          value: sku,
          name,
          sku,
          kind: 'paint',
          unit_price: '',
          label: `${sku} · ${name}`,
        };
      })
      .filter(Boolean);
    const upsellingDescOptions = (upsellingProductsData || [])
      .filter((row) => String(row.status || 'Activo').trim() !== 'Inactivo')
      .map((row) => {
        const sku = String(row.upselling_code || '').trim();
        const name = String(row.product_name || '').trim();
        if (!sku || !name) return null;
        return {
          value: sku,
          name,
          sku,
          kind: 'upselling',
          unit_price: Number(row.base_price || 0) || '',
          label: `${sku} · ${name}`,
        };
      })
      .filter(Boolean);
    const uniqueByValue = new Map();
    [...paintOptions, ...upsellingDescOptions].forEach((option) => {
      if (!uniqueByValue.has(option.value)) uniqueByValue.set(option.value, option);
    });
    return [{ value: '', label: 'Seleccionar producto', name: '', kind: '' }, ...Array.from(uniqueByValue.values())];
  }, [paintProductsData, upsellingProductsData]);
  const itemColorOptions = useMemo(() => {
    const uniqueByValue = new Map();
    (paintColorsData || []).forEach((color) => {
      const rawValue = String(color.color_name || '').trim();
      if (!rawValue) return;
      const value = normalizeColorDisplayName(rawValue);
      const code = String(color.color_code || '').trim();
      const label = code ? `${code} - ${value}` : value;
      const key = rawValue.toLowerCase();
      if (!uniqueByValue.has(key)) {
        uniqueByValue.set(key, { value, label, code });
      }
    });
    return [{ value: '', label: 'Seleccionar color' }, ...Array.from(uniqueByValue.values())];
  }, [paintColorsData]);
  const upsellingOptions = useMemo(
    () =>
      [...(upsellingProductsData || [])]
        .sort((a, b) => Number(b.id || 0) - Number(a.id || 0))
        .filter((row) => String(row.status || 'Activo').trim() !== 'Inactivo')
        .map((row) => ({
          value: String(row.upselling_code || row.id || ''),
          label: `${row.upselling_code || 'UP'} · ${row.product_name || 'Sin nombre'}${
            Number(row.base_price || 0) > 0
              ? ` · $${Number(row.base_price || 0).toLocaleString('es-CL')}`
              : ''
          }`,
        }))
        .filter((option) => option.value),
    [upsellingProductsData]
  );
  const itemFormatOptions = useMemo(() => {
    const rows = [...(paintFormatsData || [])]
      .filter((format) => String(format.display_name || '').trim())
      .sort((a, b) => {
        const sortDiff = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
        if (sortDiff !== 0) return sortDiff;
        return String(a.display_name).localeCompare(String(b.display_name), 'es');
      });
    return [
      { value: '', label: 'Seleccionar formato' },
      ...rows.map((format) => {
        const name = String(format.display_name).trim();
        const code = String(format.format_code || '').trim().toUpperCase();
        return {
          value: name,
          label: code ? `${name} (${code})` : name,
        };
      }),
    ];
  }, [paintFormatsData]);
  const defaultFormatUnit = useMemo(
    () => pickDefaultFormatUnit(paintFormatsData),
    [paintFormatsData]
  );
  const getEmptyQuotationForm = (unit = defaultFormatUnit) => ({
    ...EMPTY_QUOTATION_FORM,
    quotation_date: getTodayISO(),
    validity_days: String(DEFAULT_VALIDITY_DAYS),
    payment_terms: DEFAULT_PAYMENT_TERMS,
    quotation_items: [buildEmptyQuotationLineItem(unit)],
  });
  const colorCodeByName = useMemo(() => {
    const map = new Map();
    (paintColorsData || []).forEach((color) => {
      const rawName = String(color.color_name || '').trim();
      const displayName = normalizeColorDisplayName(rawName);
      const key = normalizeLookupKey(rawName);
      const code = String(color.color_code || '').trim().toUpperCase();
      if (!code) return;
      if (key && !map.has(key)) {
        map.set(key, code);
      }
      const displayKey = normalizeLookupKey(displayName);
      if (displayKey && !map.has(displayKey)) {
        map.set(displayKey, code);
      }
    });
    return map;
  }, [paintColorsData]);
  const buildQuotationItemSku = (item = {}) => String(item.product_sku || '').trim();

  const quotationOptions = [
    { value: '', label: 'Seleccionar cotización' },
    ...quotationsData.map((quotation) => ({
      value: String(quotation.id),
      label: quotation.quotation_code,
    })),
  ];
  const itemDescriptionOptionsForItemForm =
    itemFormData.product_sku &&
    !itemDescriptionOptions.some((option) => option.value === itemFormData.product_sku)
      ? [
          {
            value: itemFormData.product_sku,
            name: String(itemFormData.item_name || '').trim(),
            label: String(itemFormData.item_name || '').trim() || itemFormData.product_sku,
          },
          ...itemDescriptionOptions,
        ]
      : itemDescriptionOptions;
  const itemColorOptionsForItemForm =
    itemFormData.color && !itemColorOptions.some((option) => option.value === itemFormData.color)
      ? [{ value: itemFormData.color, label: itemFormData.color }, ...itemColorOptions]
      : itemColorOptions;
  const itemFormatOptionsForItemForm =
    itemFormData.unit && !itemFormatOptions.some((option) => option.value === itemFormData.unit)
      ? [{ value: itemFormData.unit, label: itemFormData.unit }, ...itemFormatOptions]
      : itemFormatOptions;

  const loadQuotations = async () => {
    setQuotationsLoading(true);
    setQuotationsError('');
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setQuotationsData(
        rawData.map((quotation) => ({
          ...quotation,
          status: normalizeQuotationStatus(quotation.status),
          user_initials: getUserInitials(quotation.user_full_name || ''),
        }))
      );
    } catch (error) {
      setQuotationsError('No se pudieron cargar las cotizaciones.');
      setQuotationsData([]);
      console.error('Error loading quotations:', error);
    } finally {
      setQuotationsLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/organizations/`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setOrganizationsData(rawData);
    } catch (error) {
      setOrganizationsData([]);
      console.error('Error loading organizations for quotations:', error);
    }
  };

  const loadContacts = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/contacts/`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setContactsData(rawData);
    } catch (error) {
      setContactsData([]);
      console.error('Error loading contacts for quotations:', error);
    }
  };

  const loadCompetitors = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/competitors/`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setCompetitorsData(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setCompetitorsData([]);
      console.error('Error loading competitors for quotations:', error);
    }
  };

  const loadSummaryOrganizationUnits = async (organizationId) => {
    const orgId = Number(organizationId || 0);
    if (!orgId) {
      setSummaryOrganizationUnits([]);
      return;
    }
    try {
      const response = await authFetch(`${API_BASE_URL}/api/organizations/${orgId}/units/`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setSummaryOrganizationUnits(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setSummaryOrganizationUnits([]);
      console.error('Error loading organization units for quotation summary:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/users/`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setUsersData(rawData);
    } catch (error) {
      setUsersData([]);
      console.error('Error loading users for quotations:', error);
    }
  };

  const loadPaintProducts = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/products`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setPaintProductsData(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setPaintProductsData([]);
      console.error('Error loading paint products for description options:', error);
    }
  };
  const loadPaintColors = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/products/colors`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setPaintColorsData(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setPaintColorsData([]);
      console.error('Error loading paint colors for color options:', error);
    }
  };
  const loadPaintFormats = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/products/formats`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setPaintFormatsData(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setPaintFormatsData([]);
      console.error('Error loading paint formats for SKU resolution:', error);
    }
  };
  const loadUpsellingProducts = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/upselling-products`, {
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setUpsellingProductsData(Array.isArray(rawData) ? rawData : []);
    } catch (error) {
      setUpsellingProductsData([]);
      console.error('Error loading upselling products for quotations form:', error);
    }
  };

  const loadUpsellingCategories = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/upselling-products/categories`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      const list = Array.isArray(rawData) ? rawData : [];
      setUpsellingCategoryOptions(list.filter((row) => !row.parent_id && !row.parent_code));
    } catch (error) {
      setUpsellingCategoryOptions([]);
      console.error('Error loading upselling categories:', error);
    }
  };

  const handleCreateUpsellingProduct = async ({ product_name, base_price, category_code }) => {
    const response = await authFetch(`${API_BASE_URL}/api/quotations/upselling-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_name: String(product_name || '').trim(),
        base_price: Number(base_price) || 0,
        category_code: category_code ? String(category_code).trim() : '',
      }),
    });
    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      if (typeof data.detail === 'string') msg = data.detail;
      else if (Array.isArray(data.detail)) {
        msg = data.detail.map((x) => (typeof x === 'string' ? x : x.msg || JSON.stringify(x))).join(' ');
      }
      throw new Error(msg);
    }
    // Actualización optimista: el nuevo ítem aparece arriba en el desplegable al instante
    if (data && (data.id != null || data.upselling_code)) {
      setUpsellingProductsData((prev) => {
        const list = Array.isArray(prev) ? [...prev] : [];
        const nid = data.id != null ? Number(data.id) : null;
        const code = data.upselling_code != null ? String(data.upselling_code).trim() : '';
        const rest = list.filter((row) => {
          if (nid != null && Number(row.id) === nid) return false;
          if (code && String(row.upselling_code || '').trim() === code) return false;
          return true;
        });
        return [data, ...rest];
      });
    }
    await loadUpsellingProducts();
    return data;
  };

  const loadItems = async () => {
    setItemsLoading(true);
    setItemsError('');
    try {
      const response = await authFetch(`${API_BASE_URL}/api/quotations/items`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const rawData = await response.json();
      setItemsData(rawData);
    } catch (error) {
      setItemsError('No se pudieron cargar los ítems.');
      setItemsData([]);
      console.error('Error loading quotation items:', error);
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    // Primero lo visible en listado/resumen; el resto en segundo plano.
    loadQuotations();
    loadOrganizations();
    loadUsers();

    const loadSecondary = async () => {
      await Promise.all([
        loadItems(),
        loadContacts(),
        loadCompetitors(),
        loadPaintProducts(),
        loadPaintColors(),
        loadPaintFormats(),
        loadUpsellingProducts(),
        loadUpsellingCategories(),
      ]);
    };
    loadSecondary();
  }, []);

  const openCreateQuotationForm = () => {
    setEditingQuotationId(null);
    setQuotationFormError('');
    setQuotationFieldErrors({});
    quotationFormDirtyRevRef.current = 0;
    setQuotationFormDirty(false);
    setDraftAutosaveStatus('idle');
    setDraftAutosaveMessage('');
    const sessionUserId = sessionUser?.id ? String(sessionUser.id) : '';
    const fallbackUserId = usersForExecutive[0]?.id ? String(usersForExecutive[0].id) : '';
    setQuotationFormData({
      ...getEmptyQuotationForm(),
      user_id: sessionUserId || fallbackUserId,
    });
    setShowQuotationFormModal(true);
  };

  const openEditQuotationForm = (quotationRow, { forceFullForm = false } = {}) => {
    const hydrateItemForForm = (item) => {
      const sku = String(item.product_sku || '').trim();
      const catalog = catalogEntryForSku(sku, paintProductsData, upsellingProductsData);
      const itemName =
        catalog?.name || String(item.item_name || item.product_name || '').trim();
      const colorCode = String(item.color_code || '').trim();
      let color = String(item.color || '').trim();
      if (!color && colorCode) {
        const colorRow = paintColorsData.find(
          (c) => String(c.color_code || '').trim().toUpperCase() === colorCode.toUpperCase()
        );
        if (colorRow) color = normalizeColorDisplayName(colorRow.color_name);
      }
      return {
        row_key: item.row_key || (item.id != null ? `qi-${item.id}` : newQuotationLineRowKey()),
        product_sku: catalog?.sku || sku,
        item_name: itemName,
        color,
        color_code: colorCode,
        quantity: String(item.quantity ?? ''),
        unit: item.unit || defaultFormatUnit,
        unit_price: String(item.unit_price ?? ''),
        discount_percent: String(item.discount_percent ?? 0),
        status: item.status || 'Activo',
      };
    };

    const normalizedStatus = normalizeQuotationStatus(quotationRow?.status);
    if (!forceFullForm && normalizedStatus && normalizedStatus !== 'Borrador') {
      setSummaryQuotation(quotationRow);
      setShowQuotationSummaryModal(true);
      setShowQuotationFormModal(false);
      void loadSummaryOrganizationUnits(quotationRow?.organization_id);
      return;
    }

    const quotationItemsForEdit = itemsData
      .filter((item) => Number(item.quotation_id) === Number(quotationRow.id))
      .map(hydrateItemForForm);

    setEditingQuotationId(quotationRow.id);
    setQuotationFormError('');
    setQuotationFieldErrors({});
    quotationFormDirtyRevRef.current = 0;
    setQuotationFormDirty(false);
    setDraftAutosaveStatus('idle');
    setDraftAutosaveMessage('');
    const editChannel = String(quotationRow.channel || '').trim();
    const editDeliveryMethod = String(quotationRow.delivery_method || 'Despacho').trim() || 'Despacho';
    setQuotationFormData({
      quotation_code: quotationRow.quotation_code || '',
      organization_id: quotationRow.organization_id ? String(quotationRow.organization_id) : '',
      contact_id: quotationRow.contact_id ? String(quotationRow.contact_id) : '',
      user_id: quotationRow.user_id ? String(quotationRow.user_id) : '',
      equivalent_gallons: String(quotationRow.equivalent_gallons ?? 0),
      quotation_date: quotationRow.quotation_date || '',
      validity_days: String(resolveValidityDays(quotationRow.validity_days)),
      tender: editChannel === 'Directo' ? '' : quotationRow.tender || '',
      channel: quotationRow.channel || '',
      delivery_days: String(quotationRow.delivery_days ?? 0),
      delivery_place:
        editDeliveryMethod === 'Retiro en sucursal'
          ? ''
          : quotationRow.delivery_place || 'Instalaciones del Cliente',
      delivery_method: editDeliveryMethod,
      payment_terms: quotationRow.payment_terms || DEFAULT_PAYMENT_TERMS,
      closing_date: quotationRow.closing_date || '',
      currency: quotationRow.currency || 'CLP',
      status: quotationRow.status || 'Borrador',
      comments_text: (quotationRow.comments || '').split(';').filter(Boolean).join('\n'),
      observations_text: quotationRow.observations || '',
      pdf_include_product_images: true,
      pdf_product_image_size_mm: '48',
      quotation_items: quotationItemsForEdit.length
        ? quotationItemsForEdit
        : [buildEmptyQuotationLineItem(defaultFormatUnit)],
    });
    setShowQuotationSummaryModal(false);
    setShowQuotationFormModal(true);
  };

  const openFullEditFromSummary = (quotationRow) => {
    const row =
      quotationsData.find((q) => Number(q.id) === Number(quotationRow?.id)) || quotationRow;
    openEditQuotationForm(row, { forceFullForm: true });
  };

  const handleCloseQuotationFromSummary = async (status, options = {}) => {
    if (!summaryQuotation?.id) {
      throw new Error('Cotización no encontrada.');
    }
    const {
      assigned_user_id: assignedUserId,
      production_items: productionItems,
      production_selections: productionSelections,
      production_notes: productionNotes,
      upselling_items: upsellingItems,
      upselling_selections: upsellingSelections,
      skip_production_request: skipProductionRequest = false,
      skip_upselling_request: skipUpsellingRequest = false,
      sale_code: saleCode,
      delivery_date: dispatchDate,
      ...statusOptions
    } = options;

    setIsClosingQuotation(true);
    try {
      const closeResult = await handleBulkUpdateQuotationsStatus(status, [summaryQuotation.id], {
        ...statusOptions,
        sale_code: saleCode,
        delivery_date: dispatchDate,
      });
      const assignedSaleCode = String(closeResult?.sale_code || saleCode || '').trim();

      if (assignedUserId) {
        await handleBulkAssignQuotations({
          quotation_ids: [summaryQuotation.id],
          user_id: assignedUserId,
        });
      }

      if (status === 'Vendida') {
        const { createProductionRequestsFromQuotationClose, createUpsellingPurchaseRequestsFromQuotationClose } = await import('./quotationCloseFulfillmentUtils');
        const responsibleUser = (usersData || []).find(
          (user) => Number(user.id) === Number(assignedUserId || summaryQuotation.user_id || 0)
        );
        if (!skipProductionRequest) {
          const productionRequests = await createProductionRequestsFromQuotationClose({
            lineItems: productionItems || [],
            selectionMap: productionSelections || {},
            quotation: summaryQuotation,
            saleCode: assignedSaleCode,
            dispatchDate,
            createdBy: responsibleUser?.full_name || summaryQuotation.user_full_name || '',
            notes: productionNotes || '',
          });
          const untouched = (productionRequests || []).find(
            (request) => request?.close_request_outcome === 'unchanged_in_production'
          );
          if (untouched) {
            window.alert(
              `La solicitud ${untouched.request_code} ya está en producción (${untouched.status}), así que no se modificó ni se creó otra. Coordina los cambios directamente con producción.`
            );
          }
        }
        if (!skipUpsellingRequest) {
          await createUpsellingPurchaseRequestsFromQuotationClose({
            lineItems: upsellingItems || [],
            selectionMap: upsellingSelections || {},
            quotation: summaryQuotation,
            saleCode: assignedSaleCode,
            dispatchDate,
            requesterName: responsibleUser?.full_name || summaryQuotation.user_full_name || '',
          });
        }
      }

      setShowQuotationSummaryModal(false);
      setSummaryQuotation(null);
      setSummaryOrganizationUnits([]);
    } finally {
      setIsClosingQuotation(false);
    }
  };

  const handleCreateContactFromSummary = async (payload) => {
    const response = await authFetch(`${API_BASE_URL}/api/contacts/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        full_name: String(payload.full_name || '').trim(),
        role: String(payload.role || '').trim(),
        email: String(payload.email || '').trim(),
        phone: String(payload.phone || '').trim(),
        linkedin: '',
        organization_id: Number(payload.organization_id || 0),
        status: payload.status || 'Activo',
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    const created = await response.json();
    await loadContacts();
    return created;
  };

  const handleCreateCompetitorFromSummary = async (payload) => {
    const response = await authFetch(`${API_BASE_URL}/api/competitors/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: String(payload.name || '').trim(),
        notes: String(payload.notes || '').trim(),
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    const created = await response.json();
    await loadCompetitors();
    return created;
  };

  const handleCreateAddressFromSummary = async (payload) => {
    const orgId = Number(payload.organization_id || 0);
    if (!orgId) throw new Error('Organización no válida.');
    const response = await authFetch(`${API_BASE_URL}/api/organizations/${orgId}/units/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        unit_name: String(payload.unit_name || '').trim() || 'Dirección de entrega',
        unit_code: '',
        address: String(payload.address || '').trim(),
        commune: String(payload.commune || '').trim(),
        region: String(payload.region || '').trim(),
        country: String(payload.country || 'Chile').trim() || 'Chile',
        notes: '',
        sort_order: 0,
      }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    const created = await response.json();
    await loadSummaryOrganizationUnits(orgId);
    return created;
  };

  const handleQuotationFormChange = (field, value) => {
    markQuotationFormDirty();
    setQuotationFieldErrors((prev) => {
      const next = { ...prev };
      if (field === 'organization_id') delete next.organization_id;
      if (field === 'user_id') delete next.user_id;
      return next;
    });
    setQuotationFormData((prev) => {
      if (field === 'organization_id') {
        const org = (organizationsData || []).find((o) => String(o.id) === String(value));
        const isB2G = String(org?.segment || '').trim().toUpperCase() === 'B2G';
        return {
          ...prev,
          organization_id: value,
          contact_id: '',
          ...(isB2G ? { payment_terms: DEFAULT_PAYMENT_TERMS } : {}),
        };
      }
      if (field === 'delivery_method') {
        const nextMethod = String(value || '').trim() || 'Despacho';
        return {
          ...prev,
          delivery_method: nextMethod,
          delivery_place:
            nextMethod === 'Retiro en sucursal'
              ? ''
              : String(prev.delivery_place || '').trim() || 'Instalaciones del Cliente',
        };
      }
      if (field === 'channel') {
        const nextChannel = String(value || '').trim();
        return {
          ...prev,
          channel: value,
          tender: nextChannel === 'Directo' ? '' : prev.tender,
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleQuotationAddItem = () => {
    markQuotationFormDirty();
    setQuotationFieldErrors((prev) => {
      const next = { ...prev };
      delete next.items;
      delete next.itemsGeneral;
      return next;
    });
    setQuotationFormData((prev) => ({
      ...prev,
      quotation_items: [
        ...(prev.quotation_items || []),
        buildEmptyQuotationLineItem(defaultFormatUnit),
      ],
    }));
  };

  const handleAddUpsellingToQuotation = (upsellingCode) => {
    const selected = (upsellingProductsData || []).find(
      (row) => String(row.upselling_code || '') === String(upsellingCode || '')
    );
    if (!selected) return;
    markQuotationFormDirty();
    setQuotationFieldErrors((prev) => {
      const next = { ...prev };
      delete next.items;
      delete next.itemsGeneral;
      return next;
    });
    setQuotationFormData((prev) => ({
      ...prev,
      quotation_items: [
        ...(prev.quotation_items || []),
        {
          row_key: newQuotationLineRowKey(),
          product_sku: String(selected.upselling_code || '').trim(),
          item_name: String(selected.product_name || '').trim(),
          color: '',
          color_code: '',
          quantity: '1',
          unit: resolveFormatDisplayName(paintFormatsData, 'Unidad'),
          unit_price: String(Number(selected.base_price || 0) || ''),
          discount_percent: '0',
          status: 'Activo',
        },
      ],
    }));
  };

  const handleQuotationRemoveItem = (index) => {
    markQuotationFormDirty();
    setQuotationFieldErrors((prev) => {
      const next = { ...prev };
      delete next.items;
      delete next.itemsGeneral;
      return next;
    });
    setQuotationFormData((prev) => {
      const items = [...(prev.quotation_items || [])];
      items.splice(index, 1);
      return {
        ...prev,
        quotation_items: items.length
          ? items
          : [buildEmptyQuotationLineItem(defaultFormatUnit)],
      };
    });
  };

  const handleQuotationDuplicateItem = (index) => {
    markQuotationFormDirty();
    setQuotationFieldErrors((prev) => {
      const next = { ...prev };
      delete next.items;
      delete next.itemsGeneral;
      return next;
    });
    setQuotationFormData((prev) => {
      const items = [...(prev.quotation_items || [])];
      const source = items[index];
      if (!source) return prev;
      items.splice(index + 1, 0, { ...source, row_key: newQuotationLineRowKey() });
      return { ...prev, quotation_items: items };
    });
  };

  const handleQuotationItemChange = (index, field, value) => {
    markQuotationFormDirty();
    setQuotationFieldErrors((prev) => {
      if (!prev.items && !prev.itemsGeneral) return prev;
      const next = { ...prev };
      delete next.itemsGeneral;
      if (next.items && next.items[index]) {
        const row = { ...next.items[index] };
        if (field === 'item_name' || field === 'product_sku') delete row.item_name;
        if (field === 'quantity') delete row.quantity;
        if (field === 'unit_price') delete row.unit_price;
        if (Object.keys(row).length === 0) {
          const { [index]: _removed, ...rest } = next.items;
          next.items = Object.keys(rest).length ? rest : undefined;
        } else {
          next.items = { ...next.items, [index]: row };
        }
      }
      if (next.items && Object.keys(next.items).length === 0) delete next.items;
      return next;
    });
    setQuotationFormData((prev) => {
      const items = [...(prev.quotation_items || [])];
      const current = items[index] || {};
      const nextRow = { ...current, [field]: value };
      if (field === 'product_sku') {
        const catalog = catalogEntryForSku(value, paintProductsData, upsellingProductsData);
        nextRow.product_sku = catalog?.sku || String(value || '').trim();
        nextRow.item_name = catalog?.name || '';
        if (catalog?.unit_price !== '' && catalog?.unit_price != null) {
          nextRow.unit_price = String(catalog.unit_price);
        }
      }
      items[index] = nextRow;
      return { ...prev, quotation_items: items };
    });
  };

  const mapQuotationItemForApi = (item) => {
    const sku = String(item.product_sku || '').trim();
    const catalog = catalogEntryForSku(sku, paintProductsData, upsellingProductsData);
    return {
      product_sku: catalog?.sku || sku,
      item_name: catalog?.name || String(item.item_name || '').trim(),
      quantity: Number(item.quantity || 0),
      unit: (item.unit || defaultFormatUnit).trim(),
      unit_price: Number(item.unit_price || 0),
      discount_percent: Number(item.discount_percent || 0),
      color: (item.color || '').trim(),
      color_code: String(item.color_code || '').trim(),
      status: item.status || 'Activo',
    };
  };

  const handleSaveQuotation = async ({ mode = 'manual' } = {}) => {
    const isAutosave = mode === 'autosave';

    if (quotationSaveLockRef.current) {
      if (isAutosave) return { ok: false, reason: 'busy' };
      // Wait briefly if an autosave is in flight so manual save uses the latest id.
      const started = Date.now();
      while (quotationSaveLockRef.current && Date.now() - started < 8000) {
        await new Promise((resolve) => window.setTimeout(resolve, 100));
      }
      if (quotationSaveLockRef.current) {
        setQuotationFormError('Hay un guardado en curso. Intenta de nuevo en un momento.');
        return { ok: false, reason: 'busy' };
      }
    }

    const formSnapshot = quotationFormDataRef.current || quotationFormData;
    const currentEditingId = editingQuotationIdRef.current ?? editingQuotationId;

    if (isAutosave) {
      if (!draftAutosaveEnabledRef.current) return { ok: false, reason: 'disabled' };
      if (!showQuotationFormModal) return { ok: false, reason: 'closed' };
      if (normalizeQuotationStatus(formSnapshot.status) !== 'Borrador') {
        return { ok: false, reason: 'not_draft' };
      }
    }

    const itemsCheck = validateQuotationLineItems(formSnapshot.quotation_items);
    const fieldErrors = {};
    if (!formSnapshot.organization_id) {
      fieldErrors.organization_id = 'Debes seleccionar una organización.';
    }
    if (!formSnapshot.user_id) {
      fieldErrors.user_id = 'Debes seleccionar el ejecutivo de la cotización.';
    }
    if (!itemsCheck.ok) {
      if (Object.keys(itemsCheck.itemErrors || {}).length) {
        fieldErrors.items = itemsCheck.itemErrors;
      }
      if (itemsCheck.itemsGeneral) {
        fieldErrors.itemsGeneral = itemsCheck.itemsGeneral;
      }
    }
    if (
      fieldErrors.organization_id ||
      fieldErrors.user_id ||
      fieldErrors.items ||
      fieldErrors.itemsGeneral
    ) {
      if (isAutosave) {
        setDraftAutosaveStatus('idle');
        setDraftAutosaveMessage('Completa organización, ejecutivo e ítems para autoguardar');
        return { ok: false, reason: 'incomplete' };
      }
      setQuotationFieldErrors(fieldErrors);
      setQuotationFormError('');
      return { ok: false, reason: 'validation' };
    }

    if (!isAutosave && itemsCheck.hasZeroValues) {
      setQuotationFieldErrors({
        items: itemsCheck.itemWarnings || {},
        itemsGeneral:
          'Advertencia: hay ítems con cantidad o precio unitario en 0.',
      });
      if (!confirmZeroValueItems(itemsCheck)) {
        return { ok: false, reason: 'cancelled' };
      }
    }

    quotationSaveLockRef.current = true;
    const dirtyRevAtStart = quotationFormDirtyRevRef.current;
    if (isAutosave) {
      setDraftAutosaveStatus('saving');
      setDraftAutosaveMessage('Autoguardando…');
    } else {
      setIsSavingQuotation(true);
      setQuotationFormError('');
      setQuotationFieldErrors({});
    }

    const selectedOrganization = organizationOptions.find(
      (option) => option.value === formSnapshot.organization_id
    );
    const saveChannel = String(formSnapshot.channel || '').trim();
    const saveDeliveryMethod =
      String(formSnapshot.delivery_method || 'Despacho').trim() || 'Despacho';

    const payload = {
      quotation_code: resolveQuotationCodeForPayload(formSnapshot.quotation_code),
      organization_id: Number(formSnapshot.organization_id || 0),
      organization_name: selectedOrganization?.label || '',
      contact_id: Number(formSnapshot.contact_id || 0),
      user_id: Number(formSnapshot.user_id || 0),
      equivalent_gallons: Number(formSnapshot.equivalent_gallons || 0),
      quotation_date: formSnapshot.quotation_date || '',
      validity_days: resolveValidityDays(formSnapshot.validity_days),
      tender: saveChannel === 'Directo' ? '' : formSnapshot.tender || '',
      channel: formSnapshot.channel || '',
      delivery_days: Number(formSnapshot.delivery_days || 0),
      delivery_place:
        saveDeliveryMethod === 'Retiro en sucursal' ? '' : formSnapshot.delivery_place || '',
      delivery_method: saveDeliveryMethod,
      payment_terms: formSnapshot.payment_terms || DEFAULT_PAYMENT_TERMS,
      closing_date: formSnapshot.closing_date || '',
      currency: formSnapshot.currency || 'CLP',
      status: isAutosave ? 'Borrador' : formSnapshot.status || 'Borrador',
      comments: (formSnapshot.comments_text || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .join(';'),
      observations: (formSnapshot.observations_text || '').trim(),
      quotation_items: itemsCheck.items.map(mapQuotationItemForApi),
    };

    const endpoint = currentEditingId
      ? `${API_BASE_URL}/api/quotations/${currentEditingId}`
      : `${API_BASE_URL}/api/quotations/`;
    const method = currentEditingId ? 'PUT' : 'POST';

    try {
      const response = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      const saved = await response.json().catch(() => ({}));
      const savedId = saved?.id || currentEditingId;
      const savedCode = saved?.quotation_code;

      if (savedId && !currentEditingId) {
        setEditingQuotationId(savedId);
        editingQuotationIdRef.current = savedId;
      }
      if (savedCode) {
        setQuotationFormData((prev) =>
          prev.quotation_code === savedCode
            ? prev
            : { ...prev, quotation_code: savedCode }
        );
      }

      if (quotationFormDirtyRevRef.current === dirtyRevAtStart) {
        setQuotationFormDirty(false);
      } else {
        setQuotationFormDirty(true);
      }
      await loadQuotations();
      await loadItems();

      if (isAutosave) {
        const timeLabel = new Date().toLocaleTimeString('es-CL', {
          hour: '2-digit',
          minute: '2-digit',
        });
        if (quotationFormDirtyRevRef.current === dirtyRevAtStart) {
          setDraftAutosaveStatus('saved');
          setDraftAutosaveMessage(`Autoguardado ${timeLabel}`);
        } else {
          setDraftAutosaveStatus('pending');
          setDraftAutosaveMessage(`Autoguardado ${timeLabel} · hay cambios nuevos`);
        }
        return { ok: true, id: savedId };
      }

      setShowQuotationFormModal(false);
      setEditingQuotationId(null);
      setQuotationFormData(getEmptyQuotationForm());
      setDraftAutosaveStatus('idle');
      setDraftAutosaveMessage('');
      return { ok: true, id: savedId };
    } catch (error) {
      const message = error.message || 'No se pudo guardar la cotización.';
      if (isAutosave) {
        setDraftAutosaveStatus('error');
        setDraftAutosaveMessage(message);
        return { ok: false, reason: 'error', error: message };
      }
      setQuotationFieldErrors({});
      setQuotationFormError(message);
      return { ok: false, reason: 'error', error: message };
    } finally {
      quotationSaveLockRef.current = false;
      if (!isAutosave) {
        setIsSavingQuotation(false);
      }
    }
  };

  useEffect(() => {
    if (!showQuotationFormModal || !draftAutosaveEnabled || !quotationFormDirty) {
      return undefined;
    }
    if (normalizeQuotationStatus(quotationFormData.status) !== 'Borrador') {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      void handleSaveQuotation({ mode: 'autosave' });
    }, QUOTATION_DRAFT_AUTOSAVE_MS);

    return () => window.clearTimeout(timer);
    // handleSaveQuotation closes over latest helpers; debounce intentionally on form dirty signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showQuotationFormModal,
    draftAutosaveEnabled,
    quotationFormDirty,
    quotationFormData,
  ]);

  const loadLogoDataUrl = () =>
    new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = image.width || 220;
          canvas.height = image.height || 90;
          const context = canvas.getContext('2d');
          if (!context) {
            resolve(null);
            return;
          }
          context.drawImage(image, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          resolve(null);
        }
      };
      image.onerror = () => resolve(null);
      image.src = '/assets/soquimat-logo.png';
    });

  const handleEmitQuotationPdf = async () => {
    if (!quotationFormData.organization_id) {
      setQuotationFormError('Debes seleccionar una organización antes de emitir.');
      return;
    }
    if (!quotationFormData.user_id) {
      setQuotationFormError('Debes seleccionar el ejecutivo de la cotización antes de emitir.');
      return;
    }

    const itemsCheck = validateQuotationLineItems(quotationFormData.quotation_items);
    if (!itemsCheck.ok) {
      const fieldErrors = {};
      if (Object.keys(itemsCheck.itemErrors || {}).length) {
        fieldErrors.items = itemsCheck.itemErrors;
      }
      if (itemsCheck.itemsGeneral) {
        fieldErrors.itemsGeneral = itemsCheck.itemsGeneral;
      }
      setQuotationFieldErrors(fieldErrors);
      setQuotationFormError('Corrige los ítems antes de emitir.');
      return;
    }
    if (itemsCheck.hasZeroValues) {
      setQuotationFieldErrors({
        items: itemsCheck.itemWarnings || {},
        itemsGeneral:
          'Advertencia: hay ítems con cantidad o precio unitario en 0.',
      });
      if (!confirmZeroValueItems(itemsCheck)) {
        return;
      }
    }
    setQuotationFieldErrors({});

    const organizationLabel =
      organizationOptions.find((option) => String(option.value) === String(quotationFormData.organization_id))?.label ||
      '';
    const selectedContact =
      contactsData.find((contact) => String(contact.id) === String(quotationFormData.contact_id)) || null;
    const selectedOrganization =
      organizationsData.find((organization) => String(organization.id) === String(quotationFormData.organization_id)) || null;
    const selectedUser =
      usersForExecutive.find((user) => String(user.id) === String(quotationFormData.user_id)) ||
      (sessionUser && String(sessionUser.id) === String(quotationFormData.user_id)
        ? sessionUser
        : null);
    const contactLabel = selectedContact?.full_name || '';
    const clientAddress = [selectedOrganization?.address, selectedOrganization?.commune, selectedOrganization?.region]
      .filter(Boolean)
      .join(', ') || 'S/I';
    const clientEmail = selectedContact?.email || 'S/I';
    const clientPhone = selectedContact?.phone || 'S/I';
    const executiveName = selectedUser?.full_name || 'S/I';
    const executiveAddress = 'Anibal Pinto #340, Concepcion';
    const executiveEmail = selectedUser?.email || 'S/I';
    const executivePhone = selectedUser?.fono || 'S/I';
    const executiveOrgRut = '77.112.762-2';
    const clientOrgRut = selectedOrganization?.rut || 'S/I';

    const itemsForPdf = editingQuotationId
      ? itemsData.filter((item) => Number(item.quotation_id) === Number(editingQuotationId))
      : itemsCheck.items.map((item) => {
            const quantity = Number(item.quantity || 0);
            const unitPrice = Number(item.unit_price || 0);
            const discount = Number(item.discount_percent || 0);
            const lineTotal = quantity * unitPrice * (1 - Math.max(Math.min(discount, 100), 0) / 100);
            return {
              item_name: catalogEntryForSku(
                item.product_sku,
                paintProductsData,
                upsellingProductsData
              )?.name || item.item_name || '',
              color: item.color || '',
              quantity,
              unit: item.unit || defaultFormatUnit,
              unit_price: unitPrice,
              discount_percent: discount,
              line_total: lineTotal,
            };
          });

    if (!itemsForPdf.length) {
      setQuotationFormError('Debes tener al menos un ítem para emitir el PDF.');
      return;
    }

    const currentStatus = normalizeQuotationStatus(quotationFormData.status);
    // Solo Borrador (o sin estado) pasa a Emitida. En Adjudicada/Vendida/etc. solo se regenera el PDF.
    const shouldMarkAsIssued = !currentStatus || currentStatus === 'Borrador';
    const statusForPdf = shouldMarkAsIssued ? 'Emitida' : currentStatus;

    let pdfFormData = { ...quotationFormData, status: statusForPdf };
    setQuotationFormError('');
    setQuotationFieldErrors({});
    try {
      if (editingQuotationId) {
        if (shouldMarkAsIssued) {
          const statusRes = await authFetch(`${API_BASE_URL}/api/quotations/bulk/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              status: 'Emitida',
              quotation_ids: [Number(editingQuotationId)],
            }),
          });
          if (!statusRes.ok) {
            const errData = await statusRes.json().catch(() => ({}));
            const msg =
              typeof errData.detail === 'string' ? errData.detail : `HTTP ${statusRes.status}`;
            throw new Error(msg);
          }
          setQuotationFormData((prev) => ({ ...prev, status: 'Emitida' }));
          await loadQuotations();
          await loadItems();
        }
      } else {
        const selectedOrganization = organizationOptions.find(
          (option) => option.value === quotationFormData.organization_id
        );
        const emitChannel = String(quotationFormData.channel || '').trim();
        const emitDeliveryMethod =
          String(quotationFormData.delivery_method || 'Despacho').trim() || 'Despacho';
        const payload = {
          quotation_code: resolveQuotationCodeForPayload(quotationFormData.quotation_code),
          organization_id: Number(quotationFormData.organization_id || 0),
          organization_name: selectedOrganization?.label || '',
          contact_id: Number(quotationFormData.contact_id || 0),
          user_id: Number(quotationFormData.user_id || 0),
          equivalent_gallons: Number(quotationFormData.equivalent_gallons || 0),
          quotation_date: quotationFormData.quotation_date || '',
          validity_days: resolveValidityDays(quotationFormData.validity_days),
          tender: emitChannel === 'Directo' ? '' : quotationFormData.tender || '',
          channel: quotationFormData.channel || '',
          delivery_days: Number(quotationFormData.delivery_days || 0),
          delivery_place:
            emitDeliveryMethod === 'Retiro en sucursal'
              ? ''
              : quotationFormData.delivery_place || '',
          delivery_method: emitDeliveryMethod,
          payment_terms: quotationFormData.payment_terms || DEFAULT_PAYMENT_TERMS,
          closing_date: quotationFormData.closing_date || '',
          currency: quotationFormData.currency || 'CLP',
          status: statusForPdf,
          comments: (quotationFormData.comments_text || '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
            .join(';'),
          observations: (quotationFormData.observations_text || '').trim(),
          quotation_items: itemsCheck.items.map(mapQuotationItemForApi),
        };
        const response = await authFetch(`${API_BASE_URL}/api/quotations/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `HTTP ${response.status}`);
        }
        const created = await response.json();
        pdfFormData = {
          ...quotationFormData,
          quotation_code: created.quotation_code || quotationFormData.quotation_code,
          status: statusForPdf,
        };
        setEditingQuotationId(created.id);
        setQuotationFormData((prev) => ({
          ...prev,
          quotation_code: created.quotation_code || prev.quotation_code,
          status: statusForPdf,
        }));
        await loadQuotations();
        await loadItems();
      }
    } catch (error) {
      setQuotationFormError(
        error.message ||
          (shouldMarkAsIssued
            ? 'No se pudo registrar la cotización como emitida.'
            : 'No se pudo generar el PDF de la cotización.')
      );
      return;
    }

    const total = itemsForPdf.reduce((acc, item) => acc + Number(item.line_total || 0), 0);
    const subtotalAmount = Math.round(total);
    const ivaAmount = Math.round(total * 0.19);
    const grandTotalAmount = subtotalAmount + ivaAmount;
    const totalQuotedUnits = itemsForPdf.reduce((acc, item) => acc + Number(item.quantity || 0), 0);
    const commentsLines = (pdfFormData.comments_text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    const pdfChannel = String(pdfFormData.channel || '').trim();
    const pdfTender = String(pdfFormData.tender || '').trim();
    if (pdfChannel && pdfChannel.toLowerCase() !== 'directo') {
      const channelComment = pdfTender
        ? `Cotización correspondiente al canal ${pdfChannel}, requerimiento ${pdfTender}.`
        : `Cotización correspondiente al canal ${pdfChannel}.`;
      commentsLines.unshift(channelComment);
    }

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoDataUrl = await loadLogoDataUrl();
    const headerRightX = pageWidth - 18;
    const logoTopY = 5.5;
    const headerBlockBaseY = logoTopY + 10;
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', 14, logoTopY, 67.6, 31.2);
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(`COTIZACION N° ${pdfFormData.quotation_code || 'Por asignar'}`, headerRightX, headerBlockBaseY, {
      align: 'right',
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Emitida el: ${pdfFormData.quotation_date || getTodayISO()}`, headerRightX, headerBlockBaseY + 6, {
      align: 'right',
    });
    doc.text(`Validez: ${resolveValidityDays(pdfFormData.validity_days)} dias`, headerRightX, headerBlockBaseY + 11, {
      align: 'right',
    });

    const detailsTopY = 40;
    const { detailsBottomY } = drawQuotationContactHeader(doc, {
      pageWidth,
      organizationLabel,
      clientOrgRut,
      contactName: contactLabel,
      clientEmail,
      clientPhone,
      clientAddress,
      executiveName,
      executiveEmail,
      executivePhone,
      executiveAddress,
      executiveOrgRut,
      detailsTopY,
    });

    const drawWrappedInfoLine = (x, y, label, value, blockWidth = 118, lineHeight = 4.1) => {
      const labelText = `${label}:`;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.4);
      doc.text(labelText, x, y);
      const labelWidth = doc.getTextWidth(labelText);
      const valueX = x + labelWidth + 1.2;
      const availableWidth = Math.max(20, blockWidth - (valueX - x));
      doc.setFont('helvetica', 'normal');
      const wrappedValue = doc.splitTextToSize(String(value || 'S/I'), availableWidth);
      if (wrappedValue.length > 0) {
        doc.text(wrappedValue[0], valueX, y);
      }
      for (let i = 1; i < wrappedValue.length; i += 1) {
        doc.text(wrappedValue[i], valueX, y + i * lineHeight);
      }
      return Math.max(1, wrappedValue.length) * lineHeight;
    };

    const itemRows = itemsForPdf.map((item, idx) => [
      idx + 1,
      item.item_name || '',
      item.color || '-',
      Number(item.quantity || 0).toLocaleString('es-CL'),
      item.unit || '',
      `$ ${Number(item.unit_price || 0).toLocaleString('es-CL')}`,
      `${Number(item.discount_percent || 0)}%`,
      `$ ${Number(item.line_total || 0).toLocaleString('es-CL')}`,
    ]);

    const preTableTextY = detailsBottomY + 9;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.2);
    const preTableText = `Mediante este documento tenemos el agrado de cotizar ${Number(totalQuotedUnits).toLocaleString('es-CL')} productos según detalle a continuación:`;
    const preTableLines = doc.splitTextToSize(preTableText, pageWidth - 28);
    doc.text(preTableLines, 14, preTableTextY, { lineHeightFactor: 1.45 });

    autoTable(doc, {
      startY: preTableTextY + preTableLines.length * 4.6 + 3,
      head: [['ID', 'Producto', 'Color', 'Cant.', 'Formato', 'Unitario', 'Dcto', 'Total']],
      body: itemRows,
      showHead: 'everyPage',
      styles: { fontSize: 6.5, cellPadding: 1.2, halign: 'center', overflow: 'linebreak' },
      headStyles: {
        fillColor: [20, 30, 44],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7,
        cellPadding: 1.4,
      },
      bodyStyles: { fontSize: 6.5, cellPadding: 1.2 },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 'auto', halign: 'left' },
        2: { cellWidth: 24, halign: 'center' },
        3: { cellWidth: 14, halign: 'center' },
        4: { cellWidth: 22, halign: 'center' },
        5: { cellWidth: 22, halign: 'center' },
        6: { cellWidth: 12, halign: 'center' },
        7: { cellWidth: 24, halign: 'right' },
      },
      margin: { left: 14, right: 14, bottom: 16 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 1) {
          data.cell.styles.halign = 'left';
        }
      },
    });

    // Condiciones (izq.) + totales (der.) alineados en el mismo bloque.
    const summaryBlockNeeded = 34;
    let blockTop = (doc.lastAutoTable?.finalY || detailsBottomY + 40) + 6;
    blockTop = ensurePdfVerticalSpace(doc, blockTop, summaryBlockNeeded);

    const totalsColWidth = 64;
    const totalsColX = pageWidth - 14 - totalsColWidth;
    const infoBlockWidth = Math.max(70, totalsColX - 14 - 8);
    const infoStep = 4.2;

    autoTable(doc, {
      startY: blockTop,
      margin: { left: totalsColX, right: 14 },
      tableWidth: totalsColWidth,
      body: [
        [
          { content: 'SUBTOTAL', styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] } },
          {
            content: `$ ${subtotalAmount.toLocaleString('es-CL')}`,
            styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] },
          },
        ],
        [
          { content: 'IVA (19%)', styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] } },
          {
            content: `$ ${ivaAmount.toLocaleString('es-CL')}`,
            styles: { halign: 'right', fontStyle: 'bold', fillColor: [247, 247, 247] },
          },
        ],
        [
          { content: 'TOTAL', styles: { halign: 'right', fontStyle: 'bold', fillColor: [235, 235, 235] } },
          {
            content: `$ ${grandTotalAmount.toLocaleString('es-CL')}`,
            styles: { halign: 'right', fontStyle: 'bold', fillColor: [235, 235, 235] },
          },
        ],
      ],
      theme: 'plain',
      styles: { fontSize: 8, cellPadding: 1.6, textColor: [0, 0, 0] },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 36 },
      },
    });
    const totalsBottomY = doc.lastAutoTable?.finalY || blockTop + 20;

    let infoY = blockTop + 4;
    drawWrappedInfoLine(
      14,
      infoY,
      'Validez Cotizacion',
      `${resolveValidityDays(pdfFormData.validity_days)} Dias`,
      infoBlockWidth,
      infoStep
    );
    infoY += infoStep;
    drawWrappedInfoLine(
      14,
      infoY,
      'Forma de Pago',
      pdfFormData.payment_terms || DEFAULT_PAYMENT_TERMS,
      infoBlockWidth,
      infoStep
    );
    infoY += infoStep;
    drawWrappedInfoLine(
      14,
      infoY,
      'Plazo de Entrega',
      `${pdfFormData.delivery_days || 0} Dias`,
      infoBlockWidth,
      infoStep
    );
    infoY += infoStep;
    const lastInfoHeight = drawWrappedInfoLine(
      14,
      infoY,
      'Método de Entrega',
      String(pdfFormData.delivery_method || '').trim() || 'Despacho',
      infoBlockWidth,
      infoStep
    );
    let cursorY = Math.max(infoY + lastInfoHeight, totalsBottomY) + 6;

    if (pdfFormData.pdf_include_product_images !== false) {
      const imageSizeMm = Number(pdfFormData.pdf_product_image_size_mm) || 48;
      cursorY = await drawQuotedProductImagesOnPdf(
        doc,
        itemsForPdf,
        paintProductsData,
        cursorY,
        pageWidth,
        imageSizeMm
      );
    }

    const commentsText = commentsLines.length
      ? commentsLines.map((line, index) => `${index + 1}) ${line}`).join('\n')
      : 'Sin comentarios.';
    const commentBoxWidth = pageWidth - 28;
    const commentPadX = 3.5;
    const commentPadTop = 4.5;
    const commentFontSize = 7.2;
    const commentLineHeight = 3.6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(commentFontSize);
    const wrappedComments = doc.splitTextToSize(
      commentsText,
      Math.max(20, commentBoxWidth - commentPadX * 2)
    );
    const commentBoxHeight = Math.max(
      16,
      commentPadTop + 2 + wrappedComments.length * commentLineHeight
    );
    cursorY = ensurePdfVerticalSpace(doc, cursorY + 2, 8 + commentBoxHeight + 20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('Comentarios:', 14, cursorY);
    const commentBoxX = 14;
    const commentBoxY = cursorY + 2;
    doc.setDrawColor(90, 90, 90);
    doc.setLineWidth(0.35);
    doc.rect(commentBoxX, commentBoxY, commentBoxWidth, commentBoxHeight);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(commentFontSize);
    doc.text(wrappedComments, commentBoxX + commentPadX, commentBoxY + commentPadTop, {
      lineHeightFactor: 1.25,
    });

    const paymentInfoY = ensurePdfVerticalSpace(doc, commentBoxY + commentBoxHeight + 7, 14);
    doc.setFontSize(8.2);
    doc.setFont('helvetica', 'bold');
    doc.text('Datos de transferencia:', 14, paymentInfoY);
    doc.setFont('helvetica', 'normal');
    doc.text(
      'Razon Social: SOQUIMAT SpA | RUT: 77.112.762-2 | Banco de Chile | N° Cta Corriente: 00-225-55423-02',
      14,
      paymentInfoY + 4.2
    );
    doc.text('Envio comprobante: finanzas@soquimat.cl', 14, paymentInfoY + 8.4);

    doc.save(
      buildQuotationPdfFilename({
        org_code: selectedOrganization?.org_code,
        organization_name: organizationLabel || selectedOrganization?.org_name,
        region: selectedOrganization?.region,
        quotation_code: pdfFormData.quotation_code,
      })
    );
  };

  const handleBulkAssignQuotations = async (payload) => {
    const ids = Array.isArray(payload?.quotation_ids)
      ? payload.quotation_ids.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (!ids.length) {
      throw new Error('No hay cotizaciones seleccionadas.');
    }
    const body = { quotation_ids: ids };
    if (payload.user_id != null && Number(payload.user_id) > 0) {
      body.user_id = Number(payload.user_id);
    }
    if (payload.status) body.status = String(payload.status).trim();
    if (Object.prototype.hasOwnProperty.call(payload, 'payment_terms')) {
      body.payment_terms = String(payload.payment_terms ?? '').trim();
    }
    if (payload.closing_date) body.closing_date = String(payload.closing_date).trim();
    if (payload.sale_code) body.sale_code = String(payload.sale_code).trim();
    if (payload.loss_reason) body.loss_reason = String(payload.loss_reason).trim();

    const response = await authFetch(`${API_BASE_URL}/api/quotations/bulk/assign`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || `HTTP ${response.status}`);
    }
    await loadQuotations();
  };

  const handleBulkUpdateQuotationsStatus = async (status, quotationIds, options = {}) => {
    const ids = Array.isArray(quotationIds)
      ? quotationIds.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (!ids.length) {
      throw new Error('No hay cotizaciones seleccionadas.');
    }
    const body = { status, quotation_ids: ids };
    if (options && typeof options === 'object') {
      if (options.closing_date) body.closing_date = String(options.closing_date).trim();
      if (options.sale_code) body.sale_code = String(options.sale_code).trim();
      if (options.loss_reason) body.loss_reason = String(options.loss_reason).trim();
      if (options.loss_buyer_comment != null) {
        body.loss_buyer_comment = String(options.loss_buyer_comment).trim();
      }
      if (options.loss_competitor) body.loss_competitor = options.loss_competitor;
      if (typeof options.has_purchase_order === 'boolean') {
        body.has_purchase_order = options.has_purchase_order;
      }
      if (options.purchase_order_code != null) {
        body.purchase_order_code = String(options.purchase_order_code).trim();
      }
      if (options.purchase_order_date) body.purchase_order_date = String(options.purchase_order_date).trim();
      if (options.purchase_order_accepted_date) {
        body.purchase_order_accepted_date = String(options.purchase_order_accepted_date).trim();
      }
      if (options.purchase_order_amount != null && options.purchase_order_amount !== '') {
        body.purchase_order_amount = Number(options.purchase_order_amount);
      }
      if (options.purchase_contact_name != null) {
        body.purchase_contact_name = String(options.purchase_contact_name).trim();
      }
      if (options.purchase_contact_phone != null) {
        body.purchase_contact_phone = String(options.purchase_contact_phone).trim();
      }
      if (options.purchase_contact_email != null) {
        body.purchase_contact_email = String(options.purchase_contact_email).trim();
      }
      if (options.delivery_method) body.delivery_method = String(options.delivery_method).trim();
      if (options.delivery_address) body.delivery_address = String(options.delivery_address).trim();
      if (options.delivery_date) body.delivery_date = String(options.delivery_date).trim();
      if (options.expected_delivery_date) {
        body.expected_delivery_date = String(options.expected_delivery_date).trim();
      }
      if (options.delivery_code) body.delivery_code = String(options.delivery_code).trim();
      if (options.billing_document_type) {
        body.billing_document_type = String(options.billing_document_type).trim();
      }
      if (options.billing_address) body.billing_address = String(options.billing_address).trim();
      if (options.billing_giro) body.billing_giro = String(options.billing_giro).trim();
    }
    const response = await authFetch(`${API_BASE_URL}/api/quotations/bulk/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || `HTTP ${response.status}`);
    }
    const result = await response.json().catch(() => ({}));
    await loadQuotations();
    return result;
  };

  const handleDuplicateQuotations = async (quotationIds) => {
    const ids = Array.isArray(quotationIds)
      ? quotationIds.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (!ids.length) {
      throw new Error('No hay cotizaciones seleccionadas.');
    }
    const responses = await Promise.all(
      ids.map((id) =>
        authFetch(`${API_BASE_URL}/api/quotations/${id}/duplicate`, { method: 'POST' })
      )
    );
    const failed = responses.find((response) => !response.ok);
    if (failed) {
      const errData = await failed.json().catch(() => ({}));
      throw new Error(errData.detail || `HTTP ${failed.status}`);
    }
    await Promise.all([loadQuotations(), loadItems()]);
  };

  const handleNewVersionQuotations = async (quotationIds) => {
    const ids = Array.isArray(quotationIds)
      ? quotationIds.map((x) => Number(x)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (!ids.length) {
      throw new Error('No hay cotizaciones seleccionadas.');
    }
    const responses = await Promise.all(
      ids.map((id) =>
        authFetch(`${API_BASE_URL}/api/quotations/${id}/new-version`, { method: 'POST' })
      )
    );
    const failed = responses.find((response) => !response.ok);
    if (failed) {
      const errData = await failed.json().catch(() => ({}));
      const detail = errData.detail;
      throw new Error(
        typeof detail === 'string' ? detail : `HTTP ${failed.status}`
      );
    }
    await Promise.all([loadQuotations(), loadItems()]);
  };

  const handleDeleteQuotations = async (payload) => {
    const values = Array.isArray(payload) ? payload : [payload];
    const ids = values
      .map((v) => {
        if (v && typeof v === 'object' && 'id' in v) return Number(v.id);
        return Number(v);
      })
      .filter((id) => Number.isFinite(id));
    try {
      const deleteRequests = ids.map((id) => authFetch(`${API_BASE_URL}/api/quotations/${id}`, { method: 'DELETE' }));
      const responses = await Promise.all(deleteRequests);
      const hasError = responses.some((response) => !response.ok);
      if (hasError) {
        throw new Error('No se pudieron eliminar todas las cotizaciones seleccionadas.');
      }
      await loadQuotations();
      await loadItems();
    } catch (error) {
      window.alert(error.message || 'Error eliminando cotizaciones.');
    }
  };

  const openQuickOrganizationForm = () => {
    setQuickOrgFormError('');
    setQuickOrgFormData((prev) => ({
      ...prev,
      org_name: '',
      rut: '',
      industry: '',
      subindustry: '',
      address: '',
      commune: '',
      region: '',
      created_by: usersData[0]?.full_name || '',
    }));
    setShowQuickOrgFormModal(true);
  };

  const handleQuickOrgFormChange = (field, value) => {
    setQuickOrgFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveQuickOrganization = async () => {
    if (!String(quickOrgFormData.org_name || '').trim()) {
      setQuickOrgFormError('El nombre de la organización es obligatorio.');
      return;
    }
    setIsSavingQuickOrg(true);
    setQuickOrgFormError('');
    try {
      const payload = {
        org_name: normalizeProperCaseText(String(quickOrgFormData.org_name || '').trim()),
        rut: String(quickOrgFormData.rut || '').trim(),
        segment: String(quickOrgFormData.segment || 'B2B').trim(),
        channel: serializeOrganizationChannels(quickOrgFormData.channel),
        created_by: String(quickOrgFormData.created_by || '').trim(),
        website: String(quickOrgFormData.website || '').trim(),
        linkedin: String(quickOrgFormData.linkedin || '').trim(),
        industry: String(quickOrgFormData.industry || '').trim(),
        subindustry: String(quickOrgFormData.subindustry || '').trim(),
        email: String(quickOrgFormData.email || '').trim(),
        phone: String(quickOrgFormData.phone || '').trim(),
        address: normalizeProperCaseText(String(quickOrgFormData.address || '').trim()),
        commune: String(quickOrgFormData.commune || '').trim(),
        region: String(quickOrgFormData.region || '').trim(),
        country: String(quickOrgFormData.country || '').trim(),
      };
      const response = await authFetch(`${API_BASE_URL}/api/organizations/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      const created = await response.json();
      await loadOrganizations();
      await loadContacts();
      setQuotationFormData((prev) => ({
        ...prev,
        organization_id: created?.id ? String(created.id) : prev.organization_id,
        contact_id: '',
      }));
      setShowQuickOrgFormModal(false);
    } catch (error) {
      setQuickOrgFormError(error.message || 'No se pudo crear la organización.');
    } finally {
      setIsSavingQuickOrg(false);
    }
  };

  const openQuickContactForm = () => {
    if (!quotationFormData.organization_id) {
      setQuotationFormError('Selecciona una organización antes de agregar un contacto.');
      return;
    }
    setQuotationFormError('');
    setQuickContactFormError('');
    setQuickContactFormData({
      contact_code: 'Se asigna automáticamente',
      full_name: '',
      role: '',
      email: '',
      linkedin: '',
      phone: '',
      organization_id: String(quotationFormData.organization_id),
      status: 'Activo',
      source: '',
      source_details: '',
    });
    setShowQuickContactFormModal(true);
  };

  const handleQuickContactFormChange = (field, value) => {
    setQuickContactFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveQuickContact = async () => {
    if (!String(quickContactFormData.full_name || '').trim()) {
      setQuickContactFormError('El nombre del contacto es obligatorio.');
      return;
    }
    const organizationId = Number(quickContactFormData.organization_id || quotationFormData.organization_id || 0);
    if (!organizationId) {
      setQuickContactFormError('Debes seleccionar una organización.');
      return;
    }
    setIsSavingQuickContact(true);
    setQuickContactFormError('');
    try {
      const payload = {
        full_name: String(quickContactFormData.full_name || '').trim(),
        role: String(quickContactFormData.role || '').trim(),
        email: String(quickContactFormData.email || '').trim(),
        linkedin: String(quickContactFormData.linkedin || '').trim(),
        phone: String(quickContactFormData.phone || '').trim(),
        organization_id: organizationId,
        status: quickContactFormData.status || 'Activo',
        source: String(quickContactFormData.source || '').trim(),
        source_details: String(quickContactFormData.source_details || '').trim(),
      };
      const response = await authFetch(`${API_BASE_URL}/api/contacts/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      const created = await response.json();
      await loadContacts();
      setQuotationFormData((prev) => ({
        ...prev,
        organization_id: String(organizationId),
        contact_id: created?.id ? String(created.id) : prev.contact_id,
      }));
      setShowQuickContactFormModal(false);
    } catch (error) {
      setQuickContactFormError(error.message || 'No se pudo crear el contacto.');
    } finally {
      setIsSavingQuickContact(false);
    }
  };

  const openCreateItemForm = () => {
    setEditingItemId(null);
    setItemFormError('');
    setItemFormData({
      item_code: 'Se asigna automáticamente',
      quotation_id: '',
      ...buildEmptyQuotationLineItem(defaultFormatUnit),
    });
    setShowItemFormModal(true);
  };

  const openEditItemForm = (itemRow) => {
    setEditingItemId(itemRow.id);
    setItemFormError('');
    setItemFormData({
      item_code: itemRow.item_code || '',
      quotation_id: itemRow.quotation_id ? String(itemRow.quotation_id) : '',
      product_sku: catalogEntryForSku(
        itemRow.product_sku,
        paintProductsData,
        upsellingProductsData
      )?.sku || itemRow.product_sku || '',
      item_name:
        catalogEntryForSku(itemRow.product_sku, paintProductsData, upsellingProductsData)?.name ||
        itemRow.item_name ||
        '',
      color: itemRow.color || '',
      color_code: itemRow.color_code || '',
      quantity: String(itemRow.quantity ?? ''),
      unit: itemRow.unit || defaultFormatUnit,
      unit_price: String(itemRow.unit_price ?? ''),
      discount_percent: String(itemRow.discount_percent ?? 0),
      status: itemRow.status || 'Activo',
    });
    setShowItemFormModal(true);
  };

  const handleItemFormChange = (field, value) => {
    setItemFormData((prev) => {
      if (field === 'color') {
        return {
          ...prev,
          color: value,
          color_code: colorCodeByName.get(normalizeLookupKey(value)) || prev.color_code || '',
        };
      }
      if (field === 'product_sku') {
        const catalog = catalogEntryForSku(value, paintProductsData, upsellingProductsData);
        return {
          ...prev,
          product_sku: catalog?.sku || String(value || '').trim(),
          item_name: catalog?.name || '',
          unit_price:
            catalog?.unit_price !== '' && catalog?.unit_price != null
              ? String(catalog.unit_price)
              : prev.unit_price,
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSaveItem = async () => {
    if (!itemFormData.quotation_id) {
      setItemFormError('Debes seleccionar una cotización.');
      return;
    }
    if (!String(itemFormData.product_sku || '').trim()) {
      setItemFormError('Selecciona un producto del catálogo (código T-COAT o upselling).');
      return;
    }
    const itemQty = Number(itemFormData.quantity);
    const itemPrice = Number(itemFormData.unit_price);
    if (!Number.isFinite(itemQty) || itemQty <= 0) {
      setItemFormError('La cantidad debe ser mayor que cero.');
      return;
    }
    if (!Number.isFinite(itemPrice) || itemPrice <= 0) {
      setItemFormError('El precio unitario debe ser mayor que cero.');
      return;
    }

    setIsSavingItem(true);
    setItemFormError('');

    const catalog = catalogEntryForSku(
      itemFormData.product_sku,
      paintProductsData,
      upsellingProductsData
    );
    const payload = {
      quotation_id: Number(itemFormData.quotation_id || 0),
      product_sku: catalog?.sku || String(itemFormData.product_sku || '').trim(),
      item_name: catalog?.name || String(itemFormData.item_name || '').trim(),
      color: itemFormData.color.trim(),
      color_code: String(itemFormData.color_code || '').trim(),
      quantity: Number(itemFormData.quantity || 0),
      unit: itemFormData.unit.trim() || defaultFormatUnit,
      unit_price: Number(itemFormData.unit_price || 0),
      discount_percent: Number(itemFormData.discount_percent || 0),
      status: itemFormData.status || 'Activo',
    };

    const endpoint = editingItemId
      ? `${API_BASE_URL}/api/quotations/items/${editingItemId}`
      : `${API_BASE_URL}/api/quotations/items`;
    const method = editingItemId ? 'PUT' : 'POST';

    try {
      const response = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      setShowItemFormModal(false);
      setEditingItemId(null);
      setItemFormData({
        item_code: 'Se asigna automáticamente',
        quotation_id: '',
        ...buildEmptyQuotationLineItem(defaultFormatUnit),
      });
      await loadItems();
    } catch (error) {
      setItemFormError(error.message || 'No se pudo guardar el ítem.');
    } finally {
      setIsSavingItem(false);
    }
  };

  const handleDeleteItems = async (payload) => {
    const rowsToDelete = Array.isArray(payload) ? payload : [payload];
    try {
      const deleteRequests = rowsToDelete.map((row) =>
        authFetch(`${API_BASE_URL}/api/quotations/items/${row.id}`, { method: 'DELETE' })
      );
      const responses = await Promise.all(deleteRequests);
      const hasError = responses.some((response) => !response.ok);
      if (hasError) {
        throw new Error('No se pudieron eliminar todos los ítems seleccionados.');
      }
      await loadItems();
    } catch (error) {
      window.alert(error.message || 'Error eliminando ítems.');
    }
  };

  const handleBulkAssignProductItems = async (selectedRows, product) => {
    const rowsToUpdate = Array.isArray(selectedRows) ? selectedRows : [];
    const sku = String(product?.product_sku || '').trim();
    if (!rowsToUpdate.length || !sku) {
      throw new Error('Selecciona ítems y un producto válido.');
    }
    const catalog = catalogEntryForSku(sku, paintProductsData, upsellingProductsData);
    const productName =
      String(catalog?.name || product?.item_name || '').trim() || sku;
    const catalogPrice =
      catalog?.unit_price !== '' && catalog?.unit_price != null
        ? Number(catalog.unit_price)
        : null;

    const updateRequests = rowsToUpdate.map((row) => {
      const nextPrice =
        Number.isFinite(catalogPrice) && catalogPrice > 0
          ? catalogPrice
          : Number(row.unit_price || 0);
      const payload = {
        quotation_id: Number(row.quotation_id || 0),
        product_sku: catalog?.sku || sku,
        item_name: productName,
        color: String(row.color || '').trim(),
        color_code: String(row.color_code || '').trim(),
        quantity: Number(row.quantity || 0),
        unit: String(row.unit || defaultFormatUnit).trim() || defaultFormatUnit,
        unit_price: nextPrice,
        discount_percent: Number(row.discount_percent || 0),
        status: String(row.status || 'Activo').trim() || 'Activo',
      };
      return authFetch(`${API_BASE_URL}/api/quotations/items/${row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    });

    const responses = await Promise.all(updateRequests);
    const failed = [];
    for (let index = 0; index < responses.length; index += 1) {
      const response = responses[index];
      if (response.ok) continue;
      const errorData = await response.json().catch(() => ({}));
      failed.push(
        errorData.detail || `HTTP ${response.status} (ítem ${rowsToUpdate[index]?.id || '?'})`
      );
    }
    if (failed.length) {
      throw new Error(
        failed.length === responses.length
          ? `No se pudo asignar el producto: ${failed[0]}`
          : `Se asignó parcialmente. Fallaron ${failed.length}: ${failed[0]}`
      );
    }
    await loadItems();
  };

  const tabs = [
    {
      key: 'cotizaciones',
      label: 'Cotizaciones',
      icon: <FileText size={16} />,
      content: (
        <QuotationsTable3
          rows={filteredQuotationsData}
          loading={quotationsLoading}
          error={quotationsError}
          isDarkMode={isDarkMode}
          onRowOpen={openEditQuotationForm}
          onAdd={openCreateQuotationForm}
          onBulkDelete={handleDeleteQuotations}
          onBulkAssign={handleBulkAssignQuotations}
          onDuplicate={handleDuplicateQuotations}
          onNewVersion={handleNewVersionQuotations}
          userFilterOptions={quotationUserFilterOptions}
          organizationFilterOptions={quotationOrganizationFilterOptions}
          paymentTermsOptions={PAYMENT_TERMS_OPTIONS}
          gallonsByQuotationId={tcoatGallonsByQuotationId}
        />
      ),
    },
    {
      key: 'items',
      label: 'Items',
      icon: <ListOrdered size={16} />,
      content: (
        <QuotationItemsTable3
          rows={itemsTableRows}
          loading={itemsLoading}
          error={itemsError}
          isDarkMode={isDarkMode}
          onAdd={openCreateItemForm}
          onRowOpen={openEditItemForm}
          onBulkDelete={handleDeleteItems}
          onBulkAssignProduct={handleBulkAssignProductItems}
          productOptions={itemDescriptionOptions}
        />
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          alignItems: 'start',
          columnGap: '16px',
          rowGap: '12px',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <Heading level={2} isDarkMode={isDarkMode}>Cotizaciones</Heading>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            gap: '10px',
            flexWrap: 'nowrap',
            marginTop: '22px',
            justifySelf: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
            <span style={periodLabelStyle(ui)}>Período</span>
            <select
              value={periodFilter}
              onChange={(event) => handlePeriodFilterChange(event.target.value)}
              style={{ ...periodSelectStyle(ui), minWidth: '180px' }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          {periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Año</span>
              <select
                value={secondaryYear}
                onChange={(event) => setSecondaryYear(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: '110px' }}
              >
                <option value="all">Todas</option>
                {availableYears.map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          {periodFilter === 'monthly' ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={String(
                    secondaryYear === 'all'
                      ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                      : secondaryYear
                  )}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Mes</span>
                <select
                  value={secondaryMonth}
                  onChange={(event) => setSecondaryMonth(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: '100px' }}
                >
                  {MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
        </div>
      </div>
      <div
        style={{
          border: `1px solid ${ui.border}`,
          borderRadius: '10px',
          overflow: chartsCollapsed ? 'hidden' : 'visible',
          margin: '10px 0 12px',
          background: ui.panel,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '7px 10px',
            borderBottom: chartsCollapsed ? 'none' : `1px solid ${ui.border}`,
            background: ui.header,
            borderRadius: chartsCollapsed ? '10px' : '10px 10px 0 0',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '13px', color: ui.text, fontWeight: 700, lineHeight: 1.2 }}>
            Gráficos
            {itemsLoading ? (
              <span style={{ marginLeft: 8, fontWeight: 500, color: ui.muted, fontSize: 11 }}>
                Cargando detalle de ítems…
              </span>
            ) : null}
          </h3>
          <button
            type="button"
            onClick={() => setChartsCollapsed((prev) => !prev)}
            aria-expanded={!chartsCollapsed}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              border: `1px solid ${ui.border}`,
              borderRadius: '6px',
              background: ui.panel,
              color: ui.text,
              fontSize: '11px',
              fontWeight: 600,
              padding: '3px 8px',
              cursor: 'pointer',
              lineHeight: 1.2,
              height: '28px',
            }}
          >
            {chartsCollapsed ? 'Mostrar' : 'Ocultar'}
            {chartsCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
        </div>
        {!chartsCollapsed ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '12px',
              alignItems: 'start',
              overflow: 'visible',
              padding: '0 10px 4px',
            }}
          >
            <RegionSegmentStackedChart
              items={quotationChartItems}
              title="Distribución de cotizaciones"
              totalLabel="cotizaciones"
              stackDimensions={quotationChartStackDimensions}
              defaultStackKey="segment"
              filterOnlyDimensions={QUOTATION_CHART_PRODUCT_FILTERS}
              valueMetrics={QUOTATION_CHART_VALUE_METRICS}
            />
            <MonthlyDistributionChart
              periods={quotationChartPeriods}
              items={quotationChartItems}
              title="Distribución de cotizaciones"
              totalLabel="cotizaciones"
              stackDimensions={quotationChartStackDimensions}
              defaultStackKey="status"
              filterOnlyDimensions={QUOTATION_CHART_PRODUCT_FILTERS}
              valueMetrics={QUOTATION_CHART_VALUE_METRICS}
            />
          </div>
        ) : null}
      </div>
      <PeriodSummaryTable
        periods={selectedPeriod.columns}
        rows={quotationSummaryRows}
        title={selectedPeriod.title}
        collapsed={summaryCollapsed}
        onToggleCollapsed={() => setSummaryCollapsed((prev) => !prev)}
        segmentOptions={summarySegmentOptions}
        segmentVisibility={summarySegmentVisibility}
        onToggleSegment={handleToggleSummarySegment}
        onShowAllSegments={handleShowAllSummarySegments}
        onHideAllSegments={handleHideAllSummarySegments}
        metricOptions={SUMMARY_METRIC_OPTIONS}
        metric={summaryMetric}
        onMetricChange={setSummaryMetric}
      />
      <TabNavigation tabs={tabs} defaultTab="cotizaciones" isDarkMode={isDarkMode} />
      <QuotationSummaryForm
        isOpen={showQuotationSummaryModal}
        onClose={() => {
          if (isClosingQuotation) return;
          setShowQuotationSummaryModal(false);
          setSummaryQuotation(null);
          setSummaryOrganizationUnits([]);
        }}
        quotation={
          summaryQuotation
            ? quotationsData.find((q) => Number(q.id) === Number(summaryQuotation.id)) ||
              summaryQuotation
            : null
        }
        items={itemsData}
        isDarkMode={isDarkMode}
        onEdit={openFullEditFromSummary}
        onCloseQuotation={handleCloseQuotationFromSummary}
        isClosing={isClosingQuotation}
        contacts={contactsData}
        organization={
          summaryQuotation
            ? organizationsData.find(
                (org) => Number(org.id) === Number(summaryQuotation.organization_id)
              ) || null
            : null
        }
        organizationUnits={summaryOrganizationUnits}
        competitors={competitorsData}
        onCreateContact={handleCreateContactFromSummary}
        onCreateCompetitor={handleCreateCompetitorFromSummary}
        onCreateAddress={handleCreateAddressFromSummary}
        onRefreshOrgData={async () => {
          await Promise.all([
            loadContacts(),
            loadOrganizations(),
            loadSummaryOrganizationUnits(summaryQuotation?.organization_id),
          ]);
        }}
        onRefreshCompetitors={loadCompetitors}
        users={usersData}
        paintSkuSet={paintSkuSet}
        upsellingSkuSet={upsellingSkuSet}
      />
      <QuotationsForm
        isOpen={showQuotationFormModal}
        onClose={() => {
          setShowQuotationFormModal(false);
          setQuotationFieldErrors({});
          setQuotationFormError('');
          setDraftAutosaveStatus('idle');
          setDraftAutosaveMessage('');
        }}
        onSave={() => handleSaveQuotation({ mode: 'manual' })}
        isSaving={isSavingQuotation}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingQuotationId)}
        formSessionKey={editingQuotationId ? `q-${editingQuotationId}` : 'new'}
        formData={quotationFormData}
        onChange={handleQuotationFormChange}
        onAddItem={handleQuotationAddItem}
        onRemoveItem={handleQuotationRemoveItem}
        onDuplicateItem={handleQuotationDuplicateItem}
        onItemChange={handleQuotationItemChange}
        formError={quotationFormError}
        fieldErrors={quotationFieldErrors}
        draftAutosaveEnabled={draftAutosaveEnabled}
        onDraftAutosaveEnabledChange={handleDraftAutosaveToggle}
        draftAutosaveStatus={draftAutosaveStatus}
        draftAutosaveMessage={draftAutosaveMessage}
        showDraftAutosaveControls={
          normalizeQuotationStatus(quotationFormData.status) === 'Borrador'
        }
        organizationOptions={organizationOptions}
        contactOptions={contactOptions}
        userOptions={userOptions}
        itemDescriptionOptions={itemDescriptionOptions}
        itemFormatOptions={itemFormatOptions}
        paymentTermsOptions={PAYMENT_TERMS_OPTIONS}
        channelOptions={channelOptions}
        itemColorOptions={itemColorOptions}
        buildItemSku={buildQuotationItemSku}
        upsellingOptions={upsellingOptions}
        upsellingCategoryOptions={upsellingCategoryOptions}
        onAddUpsellingItem={handleAddUpsellingToQuotation}
        onCreateUpsellingProduct={handleCreateUpsellingProduct}
        onEmit={handleEmitQuotationPdf}
        onAddOrganization={openQuickOrganizationForm}
        onAddContact={openQuickContactForm}
        pdfProductImageSizeOptions={PDF_PRODUCT_IMAGE_SIZE_OPTIONS}
      />
      <OrganizationsForm
        isOpen={showQuickOrgFormModal}
        onClose={() => setShowQuickOrgFormModal(false)}
        onSave={handleSaveQuickOrganization}
        isSaving={isSavingQuickOrg}
        isDarkMode={isDarkMode}
        isEditing={false}
        formData={quickOrgFormData}
        onChange={handleQuickOrgFormChange}
        formError={quickOrgFormError}
        executiveOptions={executiveOptionsForOrganizationsForm}
        existingOrganizations={organizationsData}
        currentOrganizationId={null}
      />
      <ContactsForm
        isOpen={showQuickContactFormModal}
        onClose={() => setShowQuickContactFormModal(false)}
        onSave={handleSaveQuickContact}
        isSaving={isSavingQuickContact}
        isDarkMode={isDarkMode}
        isEditing={false}
        formData={quickContactFormData}
        onChange={handleQuickContactFormChange}
        formError={quickContactFormError}
        organizationOptions={organizationOptions}
        zIndex={11000}
      />
      <QuotationItemsForm
        isOpen={showItemFormModal}
        onClose={() => setShowItemFormModal(false)}
        onSave={handleSaveItem}
        isSaving={isSavingItem}
        isDarkMode={isDarkMode}
        isEditing={Boolean(editingItemId)}
        formData={itemFormData}
        onChange={handleItemFormChange}
        formError={itemFormError}
        quotationOptions={quotationOptions}
        itemDescriptionOptions={itemDescriptionOptionsForItemForm}
        itemFormatOptions={itemFormatOptionsForItemForm}
        itemColorOptions={itemColorOptionsForItemForm}
      />
    </div>
  );
};

export default PaintQuotationsWorkspace;
